// @ts-nocheck
/**
 * initApp.ts
 * App.vue init() メソッドの本体
 * App.vue から切り出し
 */

import store, { pushSelectionUndo } from '@/store'
import * as turf from '@turf/turf'
import axios from 'axios'
import maplibregl from 'maplibre-gl'
import debounce from 'lodash/debounce'
import { toRaw } from 'vue'

import { setupTouchScrollHandlers, setupMapProtocols, createMapInstances } from '@/js/mapSetup'
import { tuneMapInteractionSpeed } from '@/js/utils/map-speed-tuning'
import { useGsiTerrainSource } from 'maplibre-gl-gsi-terrain'
import { CompassControl } from 'maplibre-gl-compass'
import { startMapViewLogging } from '@/utils/mapViewLogger'
import { spreadsheetSyncManager } from '@/js/spreadsheetSyncManager'
import { clickCircleSource, extSource, extLayer, osmBrightSources, osmBrightLayers, zenkokuChibanzuAddLayer } from '@/js/layers'
import * as Layers from '@/js/layers'
import { tuneMapForIOS } from '@/js/utils/ios-map-tuning'
import { fetchElevation } from '@/js/utils/app'
import { getUsageTracker } from '@/js/utils/usageTracker'
import { popup, mouseMoveForPopup, triggerFullScreenPopup } from '@/js/popup'
import {
  handleFileUpload,
  hitExcludedLayers,
  highlightSpecificFeatures,
  highlightSpecificFeatures2025,
  highlightSpecificFeaturesCity,
  pngDownload,
  mapillaryWindowOpenDebounced,
  addSvgAsImage,
  setFllter360,
  markerAddAndRemove,
  featureCollectionAdd,
  startPolling,
  enableDragHandles,
  userSimaSet,
  userDxfSet,
  userKmzSet,
  userKmlSet,
  restorePhotoMemoPathArrows,
  mapillaryCreate,
  setVertex,
  vertexAndMidpoint,
  setupHighlightLayer,
  installSafePicking,
  installDrawerStabilizer,
  stopDrawerAnimations,
  LngLatToAddress,
} from '@/js/downLoad'
import { generateStartEndPointsFromGeoJSON, generateSegmentLabelGeoJSON, geojsonCreate } from '@/js/pyramid'
import {
  highlightSelectedFeature,
  openDrawFloatingWindow,
  setTextClickHandled,
  setMarkerClickHandled,
  showFeatureInfo,
  clearSelectedFeature,
  clearFeatureInfo,
} from '@/js/draw'
import drawMethods from '@/js/draw'
import { refreshRadiusHighlight } from '@/js/utils/radius-highlight'
import { attachViewOrientationPair } from '@/js/utils/view-orientation-tracker'
import attachMapRightClickMenu, {
  buildOsmEditorUrl,
  buildRapidUrl,
  buildRapidMapillaryUrl,
  buildJosmRemoteUrlByCenter,
  buildGoogleMapsSearchUrl,
  buildStreetViewUrl,
  openOrCopyOverpass,
  buildShortLinksMenu,
  buildBookmarksMenu,
  buildRoutingMenuORS,
  pushFeatureToGeoJsonSource,
  pointFeature,
  removePointUnderCursor,
  exportVisibleGeoJSON_fromMap01,
} from '@/js/utils/context-menu'
import { buildTri50Submenu } from '@/js/utils/triangle50'
import { consumeToken } from '@/js/tokenService'
import { requestAddLayers } from '@/js/layerEngine/addLayersGate'

type VM = any

// TODO: これらの関数は未実装（App.vueでも同様にインポート宣言だけ存在）
// 右クリックメニューのストリートビュー関連で使用される
function buildSVUrlSimple(lngLat: any, opts: any): string {
  const { heading = 0, pitch = 0, fov = 90 } = opts || {}
  return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lngLat.lat},${lngLat.lng}&heading=${heading}&pitch=${pitch}&fov=${fov}`
}
function setSvPin(_map: any, _lngLat: any) { /* stub */ }
function openTopRightWindowFlushHalfWidthFullHeight(url: string, opts: any) {
  const w = Math.floor(window.innerWidth / 2)
  const h = window.innerHeight
  window.open(url, opts?.name || '_blank', `width=${w},height=${h},top=0,left=${w}`)
}

export async function initApp(vm: VM) {

// HTML要素を追加
const uploadInput = document.createElement('input');
uploadInput.type = 'file';
uploadInput.id = 'simaFileInput';
uploadInput.accept = '.sim';
uploadInput.style.display = 'none';
uploadInput.addEventListener('change', handleFileUpload);
document.body.appendChild(uploadInput);


// タッチスクロール処理（mapSetup.tsに切り出し）
setupTouchScrollHandlers()

// プロトコル一括登録（mapSetup.tsに切り出し）
setupMapProtocols()

const params = await vm.parseUrlParams()

// 4画面モードの復元（ループの前に実行、小画面では復元しない）
if (params.fourScreen && !vm.$store.state.isSmall500) {
  vm.setScreenMode(4)
}

// マップ連動モードの復元
if (params.mapSyncParam !== null && params.mapSyncParam !== undefined) {
  vm.$store.state.mapSyncMode = params.mapSyncParam === 'true'
}

// スプレッドシート同期設定の復元
if (params.sheetSync) {
  try {
    const syncConfig = JSON.parse(decodeURIComponent(params.sheetSync))
    if (syncConfig && syncConfig.url) {
      spreadsheetSyncManager.clearConfig()
      spreadsheetSyncManager.saveConfig(syncConfig)
    }
  } catch (e) {
    console.error('[init] スプレッドシート同期設定の復元エラー:', e)
  }
}

// 行政界履歴の座標を復元待ちとして保存（レイヤーロード後に復元）
if (params.ghist) {
  const [ghistLng, ghistLat] = params.ghist.split(',').map(Number)
  if (!isNaN(ghistLng) && !isNaN(ghistLat)) {
    vm.$store.state.pendingGyoseiHistoryCoord = [ghistLng, ghistLat]

  }
}

// 地図インスタンス生成（mapSetup.tsに切り出し）
createMapInstances(vm, params, useGsiTerrainSource, tuneMapInteractionSpeed, startMapViewLogging)

// サムネイルマーカー常時表示の復元
vm.$store.state.thumbnailAlwaysVisible = params.thumbvis

// ストリートビュー（ペグマン）の復元
if (params.sv) {
  store.state.streetViewFlg = true
  localStorage.setItem('streetView', true)
}

// モノクロモードの復元
if (params.mono) {
  vm.$store.state.isSketchMode = true
  for (const name of ['map01', 'map02', 'map03', 'map04']) {
    const map = vm.$store.state[name]
    if (map) {
      const c = map.getContainer()
      if (c) c.classList.add('oh-sketch-mode')
    }
  }
}

// URLパラメータでスワイプ指定があればここで復元
if (params.swipeFlag) {
  if (vm.isSmall500) {
    if (!vm.s_map2Flg) {
      vm.btnClickSplit()
    }
    if (!vm.swipeBlockedNotified) {
      vm.$store.dispatch('messageDialog/open', {
        id: 'swipe-not-supported',
        title: 'スワイプ比較',
        contentHtml: '<p>小画面ではスワイプ比較を利用できません。2画面表示のみ復元しました。</p>',
        options: { icon: 'mdi-alert', iconColor: 'warning' }
      })
      vm.swipeBlockedNotified = true
    }
  } else {
    vm.restoreSwipeState(parseFloat(params.swipePos))
  }
}

// 個別マップ座標の復元（マップ連動モードがfalseの場合）
if (params.mapSyncParam === 'false' && params.mapStatesParam) {
  // マップが全て初期化された後に座標を復元
  setTimeout(() => {
    try {
      const mapStates = JSON.parse(params.mapStatesParam)
      Object.keys(mapStates).forEach(mapName => {
        const state = mapStates[mapName]
        const map = vm.$store.state[mapName]
        if (map && state) {
          map.jumpTo({
            center: [parseFloat(state.lng), parseFloat(state.lat)],
            zoom: parseFloat(state.zoom),
            pitch: parseFloat(state.pitch),
            bearing: parseFloat(state.bearing)
          })
          // storeにも保存
          vm.$store.state.mapIndividualStates[mapName] = {
            lng: parseFloat(state.lng),
            lat: parseFloat(state.lat),
            zoom: parseFloat(state.zoom),
            pitch: parseFloat(state.pitch),
            bearing: parseFloat(state.bearing)
          }
        }
      })
    } catch (e) {
      console.warn('[init] 個別マップ座標の復元に失敗:', e)
    }
  }, 500) // マップ初期化完了を待つ
}

// --------------------------------
const map = vm.$store.state.map01
// const vm = this
//----------------------------------
// iOS 向けチューニング適用（メモリ軽減のため重いレイヤを無効化）
const cleanupIOSTuning = tuneMapForIOS(map, vm.$store, {
  heavyLayerIds: [
    'oh-dem-tint',           // DEM陰影
    'hillshade',             // ヒルシェード
    'oh-buildings-3d',       // 3D建物
  ],
  disable3D: true,           // 3Dを無効化してGPU負荷軽減
  reduceLabels: true,
});
// クリーンアップ関数を保存（beforeUnmountで呼ぶ）
if (cleanupIOSTuning && typeof cleanupIOSTuning === 'function') {
  vm.iosTuningCleanup = cleanupIOSTuning;
}
function getNearbyClickCirclePointId(map, e, pixelTolerance = 20) {
  if (!e || !e.lngLat) return null;
  const clickPixel = map.project(e.lngLat);
  const source = map.getSource(clickCircleSource.iD);
  const sourceData = source && source._data ? (source._data.geojson ? source._data.geojson : source._data) : null;
  const features = (sourceData && sourceData.features) ? sourceData.features : [];
  for (const f of features) {
    if (!f.geometry || f.geometry.type !== 'Point') continue;
    const [lng2, lat2] = f.geometry.coordinates;
    const featurePixel = map.project({ lng: lng2, lat: lat2 });
    const dx = featurePixel.x - clickPixel.x;
    const dy = featurePixel.y - clickPixel.y;
    const pixelDist = Math.sqrt(dx * dx + dy * dy);
    if (pixelDist < pixelTolerance) {
      return f.properties?.id ?? null; // id プロパティを返す。なければ null
    }
  }
  return null;
}
// 該当featureのid（またはnull）を返す: Polygon版
function getPolygonFeatureIdAtClick(map, e) {
  // クリックイベントと座標がなければ null
  if (!e || !e.lngLat) return null;
  const clickLngLat = [e.lngLat.lng, e.lngLat.lat];
  // ソースから GeoJSON データを取得
  const source = map.getSource(clickCircleSource.iD);
  if (!source || !source._data) return null;
  const rawData = source._data;
  const features = (rawData && rawData.geojson) ? rawData.geojson.features : (rawData && rawData.features ? rawData.features : []);
  // 各フィーチャをチェック
  for (const feature of features) {
    const geom = feature.geometry;
    if (!geom) continue;
    const type = geom.type;
    // Polygon または MultiPolygon のみ対象
    if (type === 'Polygon' || type === 'MultiPolygon') {
      // turf.booleanPointInPolygon は両方のタイプに対応
      if (turf.booleanPointInPolygon(turf.point(clickLngLat), feature)) {
        // alert(feature.properties.id)
        return feature.properties.id || null;
      }
    }
  }
  return null;
}

// 該当featureのid（またはnull）を返す: LineString版
function getLineFeatureIdAtClickByPixel(map, e, pixelTolerance = 20) {
  if (!e || !e.lngLat) return null;
  const clickPixel = map.project(e.lngLat);
  const source = map.getSource(clickCircleSource.iD);
  const sourceData = source && source._data ? (source._data.geojson ? source._data.geojson : source._data) : null;
  const features = (sourceData && sourceData.features) ? sourceData.features : [];
  const found = features.find(f => {
    if (!f.geometry || f.geometry.type !== 'LineString') return false;
    const coords = f.geometry.coordinates;
    // 各線分ごとに最短ピクセル距離を調べる
    for (let i = 0; i < coords.length - 1; i++) {
      const p1 = map.project({ lng: coords[i][0], lat: coords[i][1] });
      const p2 = map.project({ lng: coords[i + 1][0], lat: coords[i + 1][1] });
      const d = pointToSegmentDistance(clickPixel, p1, p2);
      if (d <= pixelTolerance) return true;
    }
    return false;
  });
  return found ? found.properties.id : null;
}
// 2点間の線分への最短距離（ピクセル空間で）
function pointToSegmentDistance(pt, p1, p2) {
  const x = pt.x, y = pt.y;
  const x1 = p1.x, y1 = p1.y;
  const x2 = p2.x, y2 = p2.y;
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (dx === 0 && dy === 0) {
    // 線分が点の場合
    return Math.sqrt((x - x1) ** 2 + (y - y1) ** 2);
  }
  // t: 線分上の最近点パラメータ（0～1）
  const t = Math.max(0, Math.min(1, ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy)));
  const projX = x1 + t * dx;
  const projY = y1 + t * dy;
  return Math.sqrt((x - projX) ** 2 + (y - projY) ** 2);
}
let clickTimer = null;
const CLICK_DELAY = 150; // ms

// ラベルレイヤーをクリックしたとき
map.on('click', 'click-circle-label-layer', (e) => {
  // フリーメモモード中またはフリーメモアクティブ中は何もしない
  if (vm.$store.state.isFieldNoteMemoMode || vm.$store.state.isFieldNoteMemoActive) {
    return;
  }
  const labelFeat = e.features && e.features[0];
  if (!labelFeat) return;
  const featureId = labelFeat.properties.id;
  // ラベルフィーチャーの id プロパティを取得
  vm.$store.state.id = featureId;
  // サムネイルマーカー常時表示OFFの場合、クリックで表示する
  if (!vm.$store.state.thumbnailAlwaysVisible) {
    const el = document.getElementById(`pic-marker-${featureId}`);
    if (el && el.style.display === 'none') {
      el.style.display = '';
    }
  }
  // click-circle-sourceから該当のfeatureを取得
  const source = map.getSource(clickCircleSource.iD);
  if (!source || !source._data) return;
  const rawData = source._data;
  const features = (rawData && rawData.geojson) ? rawData.geojson.features : (rawData && rawData.features ? rawData.features : []);
  const feature = features.find(f => f.properties?.id === featureId);
  if (feature) {
    if (vm.$store.state.editEnabled) {
      console.info('[edit-mode] skip FW open on click-circle-layer (editEnabled=true)', { featureId });
      return;
    }
    // フリーメモポイントの場合はフリーメモを開く（選択エフェクトとFWは開かない）
    if (feature.properties?.isFieldNoteMemo || feature.properties?.hasMemo) {
      vm.$store.state.fieldNoteMemoPointId = featureId
      vm.$store.state.isFieldNoteMemoActive = true
      // 地物FWを明示的に閉じる
      vm.$store.commit('setFloatingVisible', { id: 'draw-floating-window', visible: false })
      vm.$store.commit('setCurrentDrawFeature', null)
      return
    }
    highlightSelectedFeature(map, featureId);
    // テキスト（type='Text'）の場合はテキスト編集FWを開く
    if (feature.properties?.type === 'Text') {
      openDrawFloatingWindow(feature);
      // draw.jsのクリックハンドラで重複処理されないようにフラグを設定
      setTextClickHandled(true);
      return;
    } else if (feature.geometry?.type === 'LineString') {
      // 線の場合はFWと情報ボックスを開く
      showFeatureInfo(feature, 'line');
      openDrawFloatingWindow(feature);
      setMarkerClickHandled(true);
    } else if (feature.geometry?.type === 'Point') {
      // 通常のポイントの場合はドローFWを開く
      openDrawFloatingWindow(feature);
      // draw.jsのクリックハンドラがFWを閉じないようフラグを設定
      setMarkerClickHandled(true);
      // DrawDrawerも開く（テキスト・円中心は除く）
      // setCurrentDrawFeature（openDrawFloatingWindow内）がshowDrawDrawer=falseにリセットするため、nextTickで開く
      const isCircleCenter = feature.properties?.isCircleCenter;
      const isText = feature.properties?.type === 'Text';
      if (!isCircleCenter && !isText) {
        vm.$store.state.drawFeature = feature;
        if (!vm.$store.state.isSmall500 || !vm.$store.state.isDraw) {
          Promise.resolve().then(() => {
            vm.$store.commit('setDrawDrawer', true);
          });
        }
      }
    }
  }
});

// 距離ラベルレイヤーをクリックしたとき（segment-label-layer）
// レイヤーが存在するか確認してからイベントを登録
const setupSegmentLabelClick = () => {
  if (!map.getLayer('segment-label-layer')) {
    // レイヤーがまだない場合は少し待ってリトライ
    setTimeout(setupSegmentLabelClick, 500);
    return;
  }
  map.on('click', 'segment-label-layer', (e) => {
    // フリーメモモード中またはフリーメモアクティブ中は何もしない
    if (vm.$store.state.isFieldNoteMemoMode || vm.$store.state.isFieldNoteMemoActive) {
      return;
    }
    const labelFeat = e.features && e.features[0];
    if (!labelFeat || !labelFeat.properties.id) return;
    const featureId = labelFeat.properties.id;
    // store.state.idを設定
    vm.$store.state.id = featureId;
    // サムネイルマーカー常時表示OFFの場合、クリックで表示する
    if (!vm.$store.state.thumbnailAlwaysVisible) {
      const el = document.getElementById(`pic-marker-${featureId}`);
      if (el && el.style.display === 'none') {
        el.style.display = '';
      }
    }
    // click-circle-sourceから該当のfeatureを取得
    const source = map.getSource(clickCircleSource.iD);
    if (!source || !source._data) return;
    const rawData = source._data;
    const features = (rawData && rawData.geojson) ? rawData.geojson.features : (rawData && rawData.features ? rawData.features : []);
    const feature = features.find(f => f.properties?.id === featureId);
    if (feature && feature.geometry?.type === 'LineString') {
      if (vm.$store.state.editEnabled) {
        console.info('[edit-mode] skip FW open on segment-label-layer (editEnabled=true)', { featureId });
        return;
      }
      // FWと情報ボックスを開く
      highlightSelectedFeature(map, featureId);
      showFeatureInfo(feature, 'line');
      openDrawFloatingWindow(feature);
    }
  });
};
setupSegmentLabelClick();

// 矢印ラベルレイヤーをクリックしたとき（arrows-endpoint-label-layer）= 線のラベル
const setupArrowsLabelClick = () => {
  if (!map.getLayer('arrows-endpoint-label-layer')) {
    setTimeout(setupArrowsLabelClick, 500);
    return;
  }
  map.on('click', 'arrows-endpoint-label-layer', (e) => {
    // フリーメモモード中またはフリーメモアクティブ中は何もしない
    if (vm.$store.state.isFieldNoteMemoMode || vm.$store.state.isFieldNoteMemoActive) {
      return;
    }
    const labelFeat = e.features && e.features[0];
    if (!labelFeat) return;
    // pairIdが元のLineStringのID
    const featureId = labelFeat.properties.pairId;
    if (!featureId) return;
    if (vm.$store.state.editEnabled) {
      console.info('[edit-mode] skip FW open on arrows-endpoint-label-layer (editEnabled=true)', { featureId });
      return;
    }
    // store.state.idを設定
    vm.$store.state.id = featureId;
    // サムネイルマーカー常時表示OFFの場合、クリックで表示する
    if (!vm.$store.state.thumbnailAlwaysVisible) {
      const el = document.getElementById(`pic-marker-${featureId}`);
      if (el && el.style.display === 'none') {
        el.style.display = '';
      }
    }
    // click-circle-sourceから該当のfeatureを取得
    const source = map.getSource(clickCircleSource.iD);
    if (!source || !source._data) return;
    const rawData = source._data;
    const features = (rawData && rawData.geojson) ? rawData.geojson.features : (rawData && rawData.features ? rawData.features : []);
    const feature = features.find(f => f.properties?.id === featureId);
    if (feature && feature.geometry?.type === 'LineString') {
      // FWと情報ボックスを開く
      highlightSelectedFeature(map, featureId);
      showFeatureInfo(feature, 'line');
      openDrawFloatingWindow(feature);
    }
  });
};
setupArrowsLabelClick();

map.on('click', (e) => {
  // フリーメモモードの場合はポイントを作成してフリーメモを開く
  if (vm.$store.state.isFieldNoteMemoMode) {
    // 既存のフリーメモポイントがクリックされたかチェック
    const clickedFeatures = map.queryRenderedFeatures(e.point)
    const existingMemoPoint = clickedFeatures.find(f =>
      f.properties?.isFieldNoteMemo === true || f.properties?.isFieldNoteMemo === 'true'
    )

    if (existingMemoPoint) {
      // 既存のフリーメモポイントを開く
      const pointId = existingMemoPoint.properties.id
      const coords = existingMemoPoint.geometry?.coordinates
      vm.$store.state.fieldNoteMemoPointId = pointId
      vm.$store.state.fieldNoteMemoCoords = coords ? { lng: coords[0], lat: coords[1] } : null
      vm.$store.state.fieldNoteMemoData = existingMemoPoint.properties.memoData || null
      // 先にisFieldNoteMemoActiveをtrueにしてからisFieldNoteMemoModeをfalseにする
      // （draw.jsのハンドラが間に実行されても選択エフェクトが出ないように）
      vm.$store.state.isFieldNoteMemoActive = true
      vm.$store.state.isFieldNoteMemoMode = false
      // カーソルを元に戻す
      const mapContainer = document.getElementById('map01')
      if (mapContainer) {
        mapContainer.classList.remove('ai-area-analysis-point-cursor')
      }
      return
    }

    // 新しいフリーメモポイントを作成
    const lng = e.lngLat.lng
    const lat = e.lngLat.lat
    const coordinates = [lng, lat]
    // ポイントを作成
    const pointId = 'memo-' + String(Math.floor(10000 + Math.random() * 90000))
    const properties = {
      id: pointId,
      pairId: pointId,
      label: 'フリーメモ',
      color: '#000000',
      'point-color': '#4CAF50',  // 緑色のポイント
      'text-size': 16,
      labelType: '0',  // 通常テキスト（中央表示）
      textAnchor: 'center',
      textJustify: 'center',
      offsetValue: [0, -1.5],  // テキストを上に表示
      'text-halo-color': 'white',
      'text-halo-width': 2,
      text: 'フリーメモ',
      isFieldNoteMemo: true,  // フリーメモポイントのフラグ
    }
    geojsonCreate(map, 'Point', coordinates, properties)
    // storeに保存
    vm.$store.state.fieldNoteMemoPointId = pointId
    vm.$store.state.fieldNoteMemoCoords = { lng, lat }
    // 先にisFieldNoteMemoActiveをtrueにしてからisFieldNoteMemoModeをfalseにする
    // （draw.jsのハンドラが間に実行されても選択エフェクトが出ないように）
    vm.$store.state.isFieldNoteMemoActive = true
    vm.$store.state.isFieldNoteMemoMode = false
    // カーソルを元に戻す
    const mapContainer = document.getElementById('map01')
    if (mapContainer) {
      mapContainer.classList.remove('ai-area-analysis-point-cursor')
    }
    return
  }

  if (vm.s_isDrawPoint) return

  // 複製モードの場合は複製処理を実行（連続複製：ESCで終了）
  if (vm.$store.state.isDrawDuplicate) {
    vm.executeDuplicate(e.lngLat.lng, e.lngLat.lat)
    return
  }

  // SIMA/DXFレイヤーのクリック検出
  const features = map.queryRenderedFeatures(e.point)
  const simaOrDxfFeature = features.find(f => {
    const layerId = f.layer?.id || ''
    return (layerId.includes('oh-sima-') || layerId.includes('oh-dxf-')) &&
           layerId.includes('-polygon-layer')
  })
  if (simaOrDxfFeature) {
    // SIMA/DXFスナックバーを表示
    vm.$store.state.snackbar = true
    return
  }

})
// サークル作成-----------------------------------------------------------------------------------
function onCircleClick(e) {
  popup(e,map,'map01',vm.s_map2Flg)
}
map.on('click', (e) => {
  // SIMA/DXFレイヤーのクリック検出
  const features = map.queryRenderedFeatures(e.point)
  const simaOrDxfFeature = features.find(f => {
    const layerId = f.layer?.id || ''
    return (layerId.includes('oh-sima-') || layerId.includes('oh-dxf-')) &&
           layerId.includes('-polygon-layer')
  })
  if (simaOrDxfFeature) {
    // SIMA/DXFスナックバーを表示
    vm.$store.state.snackbar = true
    return
  }

  const lng = e.lngLat.lng
  const lat = e.lngLat.lat
  const coordinates = [lng,lat]
  vm.$store.state.coordinates = coordinates

  const targetId = getPolygonFeatureIdAtClick(map, e)
  if (targetId) {
    vm.$store.state.id = targetId
    // サムネイルマーカー常時表示OFFの場合、クリックで表示する
    if (!vm.$store.state.thumbnailAlwaysVisible) {
      const el = document.getElementById(`pic-marker-${targetId}`);
      if (el && el.style.display === 'none') el.style.display = '';
    }
    return;
  }
  const targetId2 = getNearbyClickCirclePointId(map, e, 20)
  if (targetId2) {
    vm.$store.state.id = targetId2
    // サムネイルマーカー常時表示OFFの場合、クリックで表示する
    if (!vm.$store.state.thumbnailAlwaysVisible) {
      const el = document.getElementById(`pic-marker-${targetId2}`);
      if (el && el.style.display === 'none') el.style.display = '';
    }
    return;
  }

  // 円描画はdraw.jsの2クリックモードで処理するため、ここでは何もしない
  if (vm.s_isDrawCircle) return

})

/**
 *
 */
drawMethods()



// ⭐️⭐️⭐️頂点移動
setVertex()
vertexAndMidpoint()


// moveend: 連続パン/ズーム時に過剰発火しないようデバウンス
const debouncedMoveEnd = debounce(() => {
  requestAddLayers(vm.$store, 'map01')
  const bounds = map.getBounds()
  const sw = bounds.getSouthWest()
  const ne = bounds.getNorthEast()
  vm.$store.state.lngRange = [sw.lng, ne.lng]
  vm.$store.state.latRange = [sw.lat, ne.lat]
}, 150)
map.on('moveend', debouncedMoveEnd)

// 画面同期----------------------------------------------------------------------------------------------------------
// スロットリング用のヘルパー関数
function throttle(func, limit) {
  let lastFunc;
  let lastRan;
  return function(...args) {
    if (!lastRan) {
      func(...args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(function() {
        if ((Date.now() - lastRan) >= limit) {
          func(...args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
}

let syncing = false
function syncMaps(mapA, mapB) {
  // iphoneのtきmoveではフリーズする。moveendではフリーズしない。
  // その後フリーズしなくなったのでmoveに統一。
  let m = 'move'
  if (vm.isIphone) {
    m = 'moveend'
  }

  // rAFでフレーム単位にバッチ化（moveイベントは30+回/秒発火するため）
  let syncAtoB = 0
  let syncBtoA = 0

  mapA.on(m, () => {
    if (syncing) return
    if (!vm.s_map2Flg || !vm.$store.state.mapSyncMode) return
    if (syncAtoB) return
    syncAtoB = requestAnimationFrame(() => {
      syncAtoB = 0
      if (syncing) return
      syncing = true
      mapB.setCenter(mapA.getCenter())
      mapB.setZoom(mapA.getZoom())
      mapB.setBearing(mapA.getBearing())
      if (vm.$store.state.isPitch) mapB.setPitch(mapA.getPitch())
      syncing = false
    })
  })
  mapB.on(m, () => {
    if (syncing) return
    if (!vm.s_map2Flg || !vm.$store.state.mapSyncMode) return
    if (syncBtoA) return
    syncBtoA = requestAnimationFrame(() => {
      syncBtoA = 0
      if (syncing) return
      syncing = true
      mapA.setCenter(mapB.getCenter())
      mapA.setZoom(mapB.getZoom())
      mapA.setBearing(mapB.getBearing())
      if (vm.$store.state.isPitch) mapA.setPitch(mapB.getPitch())
      syncing = false
    })
  })
}
syncMaps(vm.$store.state.map01, vm.$store.state.map02)
// --------------
// ポインター同期（2画面 / 4画面対応）
vm.setupPointerSync()

// zoom/rotateイベント: rAFでフレーム単位にバッチ化し、DOM参照をキャッシュ
const mapEl = document.querySelector('#map00') as HTMLElement | null
let zoomRafId = 0
map.on("zoom", () => {
  if (zoomRafId) return
  zoomRafId = requestAnimationFrame(() => {
    zoomRafId = 0
    if (!mapEl) return
    mapEl.style.backgroundColor = map.getZoom() <= 4.5 ? '#1e293b' : 'rgb(240,242,245)'
  })
});

let rotateRafId = 0
let cachedNeedles: NodeListOf<Element> | null = null
map.on('rotate', () => {
  if (rotateRafId) return
  rotateRafId = requestAnimationFrame(() => {
    rotateRafId = 0
    if (!cachedNeedles || cachedNeedles.length === 0) {
      cachedNeedles = document.querySelectorAll('.c-needle')
    }
    const bearing = map.getBearing()
    cachedNeedles.forEach(el => {
      el.setAttribute('transform', `rotate(${-bearing} 50 50)`)
    })
  })
});

function getScaleRatio(map) {
  const zoom = map.getZoom();
  const dpi = 96; // 通常の画面DPI（印刷用途では 300dpi などに変えても良い）
  const inchesPerMeter = 39.37;

  // 1ピクセルあたりの地上距離（m）
  const resolution = 156543.03392804097 * Math.cos(map.getCenter().lat * Math.PI / 180) / Math.pow(2, zoom);
  const scaleDenominator = resolution * dpi * inchesPerMeter;

  return `1:${Math.round(scaleDenominator).toLocaleString()}`;
}

// ---- 簡易セマフォ ----
class Semaphore {
  constructor(max) { vm.max = max; vm.active = 0; vm.q = []; }
  async acquire() {
    if (vm.active >= vm.max) await new Promise(r => vm.q.push(r));
    vm.active++;
  }
  release() {
    vm.active--;
    const next = vm.q.shift();
    if (next) next();
  }
  async run(fn) {
    await vm.acquire();
    try { return await fn(); } finally { vm.release(); }
  }
}

const CONCURRENCY = 8;                 // 同時リクエスト上限（調整してOK）
const RETRIES = 2;                     // リトライ回数
const BASE_DELAY_MS = 250;             // バックオフ初期値
const sem = new Semaphore(CONCURRENCY);

async function fetchWithRetry(url, opts = {}, retries = RETRIES) {
  for (let i = 0; i <= retries; i++) {
    try {
      const res = await fetch(url, opts);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res;
    } catch (e) {
      if (i === retries) throw e;
      // HTTP/2 の一時的な拒否を想定して指数バックオフ
      const wait = BASE_DELAY_MS * Math.pow(2, i) + Math.floor(Math.random() * 100);
      await new Promise(r => setTimeout(r, wait));
    }
  }
}

// ---- 制限付きプロトコル（"lim+" スキーム）----
maplibregl.addProtocol('lim', (req, callback) => {
  // req.url は "lim+https://..." 形式になるので、"lim+" を取り除いた最終URLを使う
  const realUrl = req.url.replace(/^lim\+/, '');

  sem.run(async () => {
    try {
      const res = await fetchWithRetry(realUrl, { credentials: 'same-origin' });
      const cc = res.headers.get('cache-control') || null;
      const ex = res.headers.get('expires') || null;
      const ct = (res.headers.get('content-type') || '').toLowerCase();

      // 種別ごとの戻し方（Vector MVT / Glyph / JSON / 画像）
      if (ct.includes('application/json')) {
        const text = await res.text();
        callback(null, text, cc, ex);
      } else if (ct.includes('protobuf') || realUrl.endsWith('.pbf')) {
        const buf = await res.arrayBuffer();
        callback(null, buf, cc, ex);
      } else if (ct.startsWith('image/')) {
        const blob = await res.blob();
        // 画像は ImageBitmap で返すのが安全
        const bitmap = await createImageBitmap(blob);
        callback(null, bitmap, cc, ex);
      } else {
        // その他は ArrayBuffer で返す
        const buf = await res.arrayBuffer();
        callback(null, buf, cc, ex);
      }
    } catch (err) {
      callback(err);
    }
  });
  // true を返すと MapLibre に「非同期で後で callback 呼ぶよ」と伝えられる
  return { cancel: () => {}, priority: 0 };
});

enableDragHandles(map)

vm.map01.on('styleimagemissing', async (e) => {
  if (e.id.includes('--')) {
    // store.state.loadingMessage3 = 'アイコン取得中'
    // store.state.loading3 = true
    await addSvgAsImage(vm.map01, e.id, `https://kenzkenz.xsrv.jp/icon/mapillary/package_signs/${e.id}.svg`)
    // store.state.loading3 = false
  }
})
vm.$store.state.map01.on('click', 'oh-mapillary-images-3-icon', async (e) => {
  await mapillaryWindowOpenDebounced(e, 2)
})
vm.$store.state.map01.on('click', 'oh-mapillary-images-2', async (e) => {
  await mapillaryWindowOpenDebounced(e, 2)
})
vm.$store.state.map01.on('click', 'oh-mapillary-images-2-label', async (e) => {
  await mapillaryWindowOpenDebounced(e, 2)
})
vm.$store.state.map01.on('click', 'oh-mapillary-images-2-icon', async (e) => {
  await mapillaryWindowOpenDebounced(e, 2)
})
vm.$store.state.map01.on('click', 'oh-mapillary-images', (e) => {
  /**
   * こっちがマピラリ本命
   */
  mapillaryWindowOpenDebounced(e)
});
vm.map01.on('dragend', () => {
  if (vm.is360Pic) {
    if (vm.map01.getZoom() > 14) {
      setFllter360(vm.$store.state.map01)
    }
  }
})
vm.map01.on('zoomend', () => {
  if (vm.is360Pic) {
    if (vm.map01.getZoom() > 14) {
      setFllter360(vm.$store.state.map01)
    }
  }
  // ズーム変更時にマーカー表示/非表示を更新（現在は無効化）
  // markerAddAndRemove()
})
/**
 * 現在全く動いていない。
 */
// await registerMdiIcon(this.map01, {
//   name: 'lot-pin',
//   path: mdiMapMarker,
//   size: 24,
//   color: '#1976D2',
//   strokeWidth: 1,
//   strokeColor: 'rgba(255,255,255,0.8)'
// });
/**
 * 右クリックメニュー
 * @type {detach|*}
 */
vm.contextMenuObject = {
  map: store.state.map01,
  items: [
    // スケッチモード（一番上）
    {
      label: '✏️ スケッチ',
      condition: () => vm.$store.state.isEditable || vm.$store.state.isMine,
      onSelect: () => {
        const tracker = getUsageTracker()
        tracker?.trackEvent('contextmenu', 'click', 'sketch-mode', { source: 'right-click' })
        vm.toggleFreehandCanvas()
      }
    },
    // フリーメモ（コメントアウト）
    // {
    //   label: '📝 フリーメモ',
    //   condition: () => vm.$store.state.isEditable || vm.$store.state.isMine,
    //   onSelect: () => {
    //     const tracker = getUsageTracker()
    //     tracker?.trackEvent('contextmenu', 'click', 'free-memo', { source: 'right-click' })
    //     vm.$store.state.pendingFieldNoteMemoMode = true
    //     vm.toggleFreehandCanvas()
    //   }
    // },
    // 重説補助資料（フリーメモの位置に移動）
    {
      label: '📋 重説補助資料作成',
      onSelect: async () => {
        // トークン消費はダイアログ内で行う
        const tracker = getUsageTracker()
        tracker?.trackEvent('contextmenu', 'click', 'jusetsu-draft', { source: 'right-click' })
        vm.$store.commit('addFWInstance', {
          type: 'jusetsu-draft',
          data: { lng: null, lat: null }
        })
      }
    },
    // AI機能シリーズ
    {
      label: '🏠 AI物件調査レポート',
      onSelect: async () => {
        const tracker = getUsageTracker()
        tracker?.trackEvent('contextmenu', 'click', 'ai-propertyReport', { source: 'right-click' })
        vm.$store.commit('addFWInstance', {
          type: 'property-report',
          data: { lng: null, lat: null }
        })
      }
    },
    {
      label: '🏆 AI物件調査レポート+',
      onSelect: async () => {
        const tracker = getUsageTracker()
        tracker?.trackEvent('contextmenu', 'click', 'ai-propertyReport-plus', { source: 'right-click' })
        const app = vm.$root || (vm.$store.state as any).oh3App || (window as any).vueApp
        if (app && typeof app.openMultiPointRanking === 'function') {
          app.openMultiPointRanking()
        }
      }
    },
    {
      label: '👶 AI子育てレポート',
      onSelect: async () => {
        const tracker = getUsageTracker()
        tracker?.trackEvent('contextmenu', 'click', 'ai-childcareReport', { source: 'right-click' })
        vm.$store.commit('addFWInstance', {
          type: 'ai-childcare-report',
          data: { lng: null, lat: null }
        })
      }
    },
    {
      label: '🤖 AI防災リスク診断',
      onSelect: async () => {
        const tracker = getUsageTracker()
        tracker?.trackEvent('contextmenu', 'click', 'area-analysis', { source: 'right-click' })
        vm.openAreaAnalysisModeDialog()
      }
    },
    // {
    //   label: '🎯 災害シミュレーション',
    //   condition: () => canUseSimulation(this.$store.state.userId),
    //   onSelect: () => {
    //     const tracker = getUsageTracker()
    //     tracker?.trackEvent('contextmenu', 'click', 'disaster-simulation', { source: 'right-click' })
    //     this.openDisasterSimulationDialog()
    //   }
    // },
    {
      label: '🤖 AI人口プロファイル',
      onSelect: async () => {
        const tracker = getUsageTracker()
        tracker?.trackEvent('contextmenu', 'click', 'super-population', { source: 'right-click' })
        vm.openPopulationProfileModeDialog()
      }
    },
    {
      label: '🤖 AI地図チャット',
      onSelect: async () => {
        const tracker = getUsageTracker()
        tracker?.trackEvent('contextmenu', 'click', 'ai-map-chat', { source: 'right-click' })
        store.commit('setFloatingVisible', { id: 'map-compare-window', visible: true })
      }
    },
    {
      label: '🤖 AI OSM検索',
      onSelect: async () => {
        const tracker = getUsageTracker()
        tracker?.trackEvent('contextmenu', 'click', 'ai-osm-search', { source: 'right-click' })
        vm.$store.dispatch('showFloatingWindow', 'ai-osm-search-window')
      }
    },
    {
      label: '🤖 AI住所検索',
      onSelect: () => {
        const tracker = getUsageTracker()
        tracker?.trackEvent('contextmenu', 'click', 'ai-address-search', { source: 'right-click' })
        // 住所検索ウィンドウを開く
        vm.$store.dispatch('showFloatingWindow', 'address-search')
      }
    },
    // その他1: 基本機能（AIシリーズ以外）
    {
      label: 'その他1',
      children: [
        {
          label: 'スワイプ比較',
          condition: () => !vm.isSmall500 && !vm.swipeMode,
          onSelect: () => {
            getUsageTracker()?.trackEvent('contextmenu', 'click', 'swipe-compare')
            vm.toggleSwipeMode()
          }
        },
        {
          label: 'OSM色々',
          items: [
            {
              label: 'iD Editorで開く',
              onSelect: ({lngLat, map}) => {
                  getUsageTracker()?.trackEvent('contextmenu', 'click', 'osm-id-editor')
                  window.open(
                      buildOsmEditorUrl(lngLat, {zoom: map.getZoom?.() ?? 19, editor: 'id'}),
                      '_blank', 'noopener'
                  )
              }
            },
            {
              label: 'RapiDで開く',
              onSelect: ({ map, lngLat }) => {
                getUsageTracker()?.trackEvent('contextmenu', 'click', 'osm-rapid')
                const url = buildRapidUrl(lngLat, { zoom: map.getZoom?.() ?? 19 });
                window.open(url, '_blank', 'noopener');
              }
            },
            // （必要なら）
            {
              label: 'RapiD（Mapillary）',
              onSelect: ({ map, lngLat }) => {
                getUsageTracker()?.trackEvent('contextmenu', 'click', 'osm-rapid-mapillary')
                const url = buildRapidMapillaryUrl(lngLat, { zoom: map.getZoom?.() ?? 19 });
                window.open(url, '_blank', 'noopener');
              }
            },
            // ▼ ここから置換：Overpassターボ メニュー本体 -------------------------
            {
              label: 'Overpass Turbo',
              items: [
                // 通常クリック=Overpassを開く / Alt or Cmd=QLをコピー
                {
                  label: '周辺±100m（全要素）',
                  onSelect: (args) => {
                    getUsageTracker()?.trackEvent('contextmenu', 'click', 'overpass-all-100m')
                    openOrCopyOverpass({ ...args, meters: 100, feature: 'all', originalEvent: args.originalEvent })
                  }
                },
                { type: 'separator' },

                // 道路
                {
                  label: '道路 ±100m（highway=*)',
                  onSelect: (args) => {
                    getUsageTracker()?.trackEvent('contextmenu', 'click', 'overpass-highway-100m')
                    openOrCopyOverpass({ ...args, meters: 100, feature: 'highway', originalEvent: args.originalEvent })
                  }
                },
                {
                  label: '道路 ±250m（highway=*)',
                  onSelect: (args) => {
                    getUsageTracker()?.trackEvent('contextmenu', 'click', 'overpass-highway-250m')
                    openOrCopyOverpass({ ...args, meters: 250, feature: 'highway', originalEvent: args.originalEvent })
                  }
                },
                { type: 'separator' },

                // POI（amenity）
                {
                  label: 'POI ±120m（amenity=*)',
                  onSelect: (args) => {
                    getUsageTracker()?.trackEvent('contextmenu', 'click', 'overpass-amenity-120m')
                    openOrCopyOverpass({ ...args, meters: 120, feature: 'amenity', originalEvent: args.originalEvent })
                  }
                },
                {
                  label: 'POI（半径入力…）',
                  onSelect: async (args) => {
                    getUsageTracker()?.trackEvent('contextmenu', 'click', 'overpass-amenity-custom')
                    const v = Number(prompt('半径(m)：', '120'));
                    const meters = Number.isFinite(v) && v > 0 ? v : 120;
                    await openOrCopyOverpass({ ...args, meters, feature: 'amenity', originalEvent: args.originalEvent });
                  }
                }
              ]
            },
            {
              // label: 'JOSMへ送る（Remote Control）',
              label: 'JOSMへ送る',
              onSelect: ({lngLat}) => {
                  getUsageTracker()?.trackEvent('contextmenu', 'click', 'osm-josm')
                  window.open(buildJosmRemoteUrlByCenter(lngLat, 200), '_blank', 'noopener')
              }
            },
          ]
        },
        {
          label: 'Googleサービス',
          items: [
            {
              label: 'Googleマップ',
              onSelect: ({map, lngLat}) => {
                getUsageTracker()?.trackEvent('contextmenu', 'click', 'google-maps')
                // 検索URLか、中心指定URLのどちらかお好みで
                const url = buildGoogleMapsSearchUrl(lngLat);
                // const url = buildGoogleMapsUrl(lngLat, { zoom: map.getZoom() });
                window.open(url, '_blank', 'noopener');
              }
            },
            {
              label: 'ストリートビュー',
              onSelect: ({map, lngLat}) => {
                getUsageTracker()?.trackEvent('contextmenu', 'click', 'google-streetview')
                const heading = map.getBearing?.() ?? 0; // 地図の方位を活用（任意）
                const pitch = (map.getPitch?.() ?? 0) - 20; // 少し下向き
                const url = buildStreetViewUrl(lngLat, {heading, pitch, fov: 90});
                window.open(url, '_blank', 'noopener');
              }
            },
            {
              label: 'SVを別ウインドウ',
              onSelect: ({map, lngLat}) => {
                getUsageTracker()?.trackEvent('contextmenu', 'click', 'google-sv-window')
                setSvPin(map, lngLat);
                const heading = ((map?.getBearing?.() ?? 0) + 360) % 360;
                const url = buildSVUrlSimple(lngLat, {heading, pitch: -15, fov: 90});
                openTopRightWindowFlushHalfWidthFullHeight(url, {name: 'GSV-TopRight-Half'});
              }
            }
          ]
        },
        // { label: 'Mapillaryを開く', onSelect: ({ lngLat }) => window.open(buildMapillaryUrl(lngLat, map.getZoom?.() ?? 18, map.getBearing?.() ?? 0), '_blank', 'noopener') },
        { label: '画面保存', onSelect: () => {
            getUsageTracker()?.trackEvent('contextmenu', 'click', 'screen-save')
            pngDownload();
          }
        },
        { label: '方位距離エディタ', onSelect: () => {
            getUsageTracker()?.trackEvent('contextmenu', 'click', 'traverse-editor')
            vm.$store.dispatch('showFloatingWindow', 'traverse');
          }
        },
        // { label: '俯瞰 60°/0° 切替', onSelect: () => { const p = map.getPitch(); map.easeTo({ pitch: p > 30 ? 0 : 60 }); } },
        {
          label: '点追加＆削除',
          children: [
            { label: '点を追加', onSelect: ({ lngLat }) => {
                getUsageTracker()?.trackEvent('contextmenu', 'click', 'point-add')
                pushFeatureToGeoJsonSource(map, 'click-circle-source', pointFeature(lngLat, {
                  label: 'PIN',
                  color: 'red',
                  'point-color': 'red',
                  'text-size': 14,
                  offsetValue: [0.6, 0.0],
                  textAnchor: 'left',
                  textJustify: 'left',
                  labelType: '1',
                  longText: '',
                  borderRadius: '10px',
                }));
              }
            },
            { label: '点を削除', onSelect: ({ point }) => {
                getUsageTracker()?.trackEvent('contextmenu', 'click', 'point-delete')
                const ok = removePointUnderCursor(map, point, 'click-circle-source');
                if (!ok) alert('直下に削除できるピンが見つかりません');
              }
            },
          ]
        },
        {
          label: 'GeoJSON出力',
          onSelect: () => {
            getUsageTracker()?.trackEvent('contextmenu', 'click', 'geojson-export')
            exportVisibleGeoJSON_fromMap01()
          }
        },
        {
          label: 'DXF出力',
          onSelect: () => {
            vm.dialogForVisibleDXF = true
          }
        },
      ]
    },
    // その他2: 高度な機能
    {
      label: 'その他2',
      children: [
        buildTri50Submenu({ map: map }, '基準点測量50°三角形', { addAboveLayerId: 'oh-some-layer-below-tri' }),
        buildTri50Submenu({ map }, '基準点測量40°三角形', { addAboveLayerId: 'oh-some-layer-below-tri', baseAngleDeg: 40 }),
        {
          label: '簡易でURL記憶',
          // 自前で処理するなら onSelectUrl を渡す（window.open 等はしない）
          ...buildShortLinksMenu({
            metersForBBox: 200,
            onSelectUrl: (url, ctx) => {
              window.open(url, '_blank', 'noopener')
            }
          })
        },
        buildBookmarksMenu(),
        buildRoutingMenuORS({
          orsApiKey: process.env.VUE_APP_ORS_API_KEY,
          defaultProfile: 'driving-car',
          // addAboveLayerId: 'oh-some-layer-below-route', // ルートをこのレイヤの直下に
          sourceId: 'oh-route',
          lineId: 'oh-route-line'
        })
      ]
    },
  ]
}
if (vm.isContextMenu) {
  vm.detachForContextMenu = attachMapRightClickMenu(vm.contextMenuObject);
}
/**
 * ピッチとベアリングを監視
 * store.state.map01Pitch等に収録
 * @type {{map01Tracker: {readonly bearing: number, destroy: function(): void, readonly pitch: number|number}, map02Tracker: {readonly bearing: number, destroy: function(): void, readonly pitch: number|number}}}
 */
const res = attachViewOrientationPair({
  map01: vm.$store.state.map01,
  map02: vm.$store.state.map02,
  store,
  bearingIn360: false,
})
vm.map01Tracker = res.map01Tracker
vm.map02Tracker = res.map02Tracker
vm.installTouchSuppressor();

// -----------------------------------------------------------------------------------------------------------------
// on load オンロード
vm.mapNames.forEach(mapName => {
  const map = vm.$store.state[mapName]
  map.once('load', () => {
    vm.$store.state.mapReady = true
    // MDIアイコン（2.8MB）をバックグラウンドでプリロード
    import('@/js/stamps').then(m => m.preloadMdiIcons())
  })
  map.on('load',async () => {

    const isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

    if (isTouch) {
      // ピンチズームは許可したまま、回転だけ無効化
      map.touchZoomRotate.enable();         // ピンチズームは生かす
      map.touchZoomRotate.disableRotation(); // 2本指の回転ジェスチャーを無効に

      // （マウスの右ドラッグ回転も止めたいなら）
      // map.dragRotate.disable();

      // （2本指 上下でのピッチも止めたいなら）
      // if (map.touchPitch && map.touchPitch.disable) map.touchPitch.disable();
    }

    // ハイライト専用レイヤーをセットアップ（新パフォーマンス改善版）
    setupHighlightLayer(map)

    // スタンプマーカー・矢印復元用のsourcedataイベントリスナー
    // ソースにデータがセットされた時にスタンプと矢印を復元
    let stampRestoreAttempted = false
    map.on('sourcedata', (e) => {
      if (e.sourceId === 'click-circle-source' && e.isSourceLoaded && !stampRestoreAttempted) {
        stampRestoreAttempted = true

        // 少し待ってからスタンプと矢印を復元（データが完全にセットされるのを待つ）
        setTimeout(() => {
          vm.restoreStampsFromGeojson()
          // 矢印も復元
          const geojsonText = vm.$store.state.clickCircleGeojsonText
          if (geojsonText) {
            try {
              const geojson = JSON.parse(geojsonText)
              restorePhotoMemoPathArrows(map, geojson)
            } catch (err) {
              console.warn('[sourcedata] Failed to parse geojson for arrow restore:', err)
            }
          }
        }, 100)
      }
    })

    // サーバーからのデータ再取得後にスタンプマーカーを復元
    window.addEventListener('oh3-restore-stamps', () => {
      vm.restoreStampsFromGeojson()
    })

    // クリック位置に円 → 内部にある layerA, layerB の地物を拾い、idProperty でハイライト
    const refreshRadiusHighlightObj = {
      queryLayers: ['oh-homusyo-2025-kijyunten', 'oh-homusyo-2026-kijyunten'],
      idProperty: '名称',
      radiusMeters: 200,
      highlight: {
        fillColor: 'rgba(255,160,0,0.35)',
        lineColor: 'rgba(255,120,0,1.0)',
        lineWidth: 3,
        circleColor: 'rgba(255,120,0,1.0)',
        circleRadius: 6,
      },
      zoom: 16
    }
    const refreshRadiusHighlightObj2 = {
      queryLayers: ['oh-gaiku-layer'],
      idProperty: 'X座標',
      radiusMeters: 200,
      highlight: {
        fillColor: 'rgba(255,160,0,0.35)',
        lineColor: 'rgba(255,120,0,1.0)',
        lineWidth: 3,
        circleColor: 'rgba(0,0,0,1.0)',
        circleRadius: 6,
      },
      zoom: 14
    }

    let lngLat = null
    map.on('click', (e) => {
      lngLat = e.lngLat
      if (vm.$store.state.isRadius200) {
        refreshRadiusHighlight(map, lngLat, refreshRadiusHighlightObj)
      }
      if (vm.$store.state.isRadius2002) {
        refreshRadiusHighlight(map, lngLat, refreshRadiusHighlightObj2)
      }
    })
    map.on('zoomend', (e) => {
      if (lngLat) {
        if (vm.$store.state.isRadius200) {
          refreshRadiusHighlight(map, lngLat, refreshRadiusHighlightObj)
        }
        if (vm.$store.state.isRadius2002) {
          refreshRadiusHighlight(map, lngLat, refreshRadiusHighlightObj2)
        }
      }
    })

    // DrawToolインスタンス化
    // const drawTool = new DrawTool(map);

    if (vm.$store.state.isIphone) {
      installSafePicking(map)
      installDrawerStabilizer(map, {
        drawerSelector: '.point-info-drawer',  // ★必須
      });
      stopDrawerAnimations('.point-info-drawer')
    }

    // const params = await this.parseUrlParams()
    // iPadでもMapLibreのパンやズームを活かしつつイベントも拾う
    // map.getCanvas().style.touchAction = 'manipulation';

    // 行政界履歴のURL復元処理（map01のみ）
    if (mapName === 'map01') {
      map.on('sourcedata', (e) => {
        // gyosei-history-sourceがロードされたら復元処理
        if (e.sourceId === 'gyosei-history-source' && e.isSourceLoaded) {
          vm.restoreGyoseiHistoryFromUrl(map)
        }
      })
    }

    map.on('click', (e) => {
      vm.onMapClick(e);
    });
    vm.originalEnable = map.dragPan.enable;
    // 通常モード用のスケール（ドラッグ可能）
    const scale = new maplibregl.ScaleControl({
      maxWidth: 100,      // スケールバーの最大幅（ピクセル）
      unit: 'metric'      // 単位： 'metric'（メートル）または 'imperial'（マイル）
    });
    map.addControl(scale, 'bottom-left');
    // map単位でスケールの位置を管理できるようにする
    if (scale._container) {
      scale._container.dataset.mapName = mapName
      scale._container.classList.add('scale-normal-mode')
    }

    // 印刷モード用のスケール（左下固定、ドラッグ不可）
    const scalePrint = new maplibregl.ScaleControl({
      maxWidth: 100,
      unit: 'metric'
    });
    map.addControl(scalePrint, 'bottom-left');
    if (scalePrint._container) {
      scalePrint._container.dataset.mapName = mapName
      scalePrint._container.classList.add('scale-for-print-mode')
      scalePrint._container.style.display = 'none'  // 初期は非表示

      // .maplibregl-ctrl-bottom-leftから取り出して、地図コンテナの直接の子要素として配置
      // これにより、通常モード用スケールのドラッグ操作の影響を受けなくなる
      const mapContainer = map.getContainer()
      if (mapContainer && scalePrint._container.parentNode) {
        mapContainer.appendChild(scalePrint._container)
      }
    }

    // 初期化時：PC時は中央表示、スマホ時は非表示
    vm.updateScaleDisplay(false)
    // PC時はドラッグ可能にする
    vm.initScaleDraggable()

    // 4画面モード同期（iframeからの利用時）
    if (mapName === 'map01') {
      vm.setup4ScreenSync(map)
    }

    // ドローを同期
    if (mapName === 'map01') {

      vm.drawToolSet()

      const map01 = vm.$store.state.map01
      const map02 = vm.$store.state.map02
      map01.on('sourcedata', (e) => {
        // e.sourceDataType === 'content' でデータ本体の更新を検知
        if (e.sourceId === clickCircleSource.iD && e.sourceDataType === 'content') {
          const src1 = map01.getSource(clickCircleSource.iD);
          // map02, map03, map04 に同期
          const targetMaps = [
            vm.$store.state.map02,
            vm.$store.state.map03,
            vm.$store.state.map04
          ].filter(Boolean);

          if (src1) {
            const data = src1._data && src1._data.geojson ? src1._data.geojson : src1._data;
            if (data && data.type === 'FeatureCollection') {
              targetMaps.forEach(targetMap => {
                const targetSrc = targetMap.getSource(clickCircleSource.iD);
                if (targetSrc) {
                  targetSrc.setData(data);
                }
              });
            }
          }

          const e_src1 = map01.getSource('end-point-source');
          if (e_src1 && e_src1._data) {
            const e_data = e_src1._data.geojson ? e_src1._data.geojson : e_src1._data;
            if (e_data && e_data.type === 'FeatureCollection') {
              targetMaps.forEach(targetMap => {
                const targetSrc = targetMap.getSource('end-point-source');
                if (targetSrc) {
                  targetSrc.setData(e_data);
                }
              });
            }
          }

          const s_src1 = map01.getSource('segment-label-source');
          if (s_src1 && s_src1._data) {
            const s_data = s_src1._data.geojson ? s_src1._data.geojson : s_src1._data;
            if (s_data && s_data.type === 'FeatureCollection') {
              targetMaps.forEach(targetMap => {
                const targetSrc = targetMap.getSource('segment-label-source');
                if (targetSrc) {
                  targetSrc.setData(s_data);
                }
              });
            }
          }
        }
      });

      // 自動レポート生成のチェック
      if (params.autoReport === 'true') {

        // 地図の初期化を待ってからレポートダイアログを開く
        if (map01) {
          map01.once('idle', () => {
            // アイドル状態になってからレポートダイアログを開く
            setTimeout(() => {

              // 赤マーカーの座標を取得
              let lng, lat

              // 1. reportLng/reportLatパラメータを優先（最優先 - レポート専用パラメータ）
              if (params.reportLng && params.reportLat) {
                lng = parseFloat(params.reportLng)
                lat = parseFloat(params.reportLat)
              }
              // 2. clickCircleGeojsonTextから赤マーカーの座標を取得
              else if (params.clickCircleGeojsonText) {
                try {
                  const geojson = JSON.parse(params.clickCircleGeojsonText)
                  // Pointタイプのfeatureを探す（赤マーカー）
                  const pointFeature = geojson.features.find(f => f.geometry && f.geometry.type === 'Point')
                  if (pointFeature) {
                    lng = pointFeature.geometry.coordinates[0]
                    lat = pointFeature.geometry.coordinates[1]
                  }
                } catch (error) {
                  console.error('[AutoReport] Failed to parse clickCircleGeojsonText:', error)
                }
              }
              // 3. storeのclickCircleGeojsonTextから取得
              else if (vm.$store.state.clickCircleGeojsonText) {
                try {
                  const geojson = JSON.parse(vm.$store.state.clickCircleGeojsonText)
                  const pointFeature = geojson.features.find(f => f.geometry && f.geometry.type === 'Point')
                  if (pointFeature) {
                    lng = pointFeature.geometry.coordinates[0]
                    lat = pointFeature.geometry.coordinates[1]
                  }
                } catch (error) {
                  console.error('[AutoReport] Failed to parse store.clickCircleGeojsonText:', error)
                }
              }
              // 4. フォールバック: params.lng/latを使う（地図の中心座標）
              if (!lng || !lat) {
                if (params.lng && params.lat) {
                  lng = parseFloat(params.lng)
                  lat = parseFloat(params.lat)
                }
              }

              if (lng && lat) {
                // ダイアログを開く
                vm.$store.commit('setFloatingVisible', {
                  id: 'real-estate-report-window',
                  visible: true
                })

                // ダイアログが開いてから自動生成を実行
                vm.$nextTick(() => {
                  setTimeout(() => {
                    const reportDialog = vm.$refs.realEstateReportDialog
                    if (reportDialog) {
                      reportDialog.autoGenerateReport(lng, lat, params.reportRadius)
                    } else {
                      console.error('[AutoReport] reportDialog ref not found')
                    }
                  }, 500)
                })
              } else {
                console.warn('[AutoReport] No coordinates found for auto-report generation')
                // 座標がない場合でもダイアログは開く
                vm.$store.commit('setFloatingVisible', {
                  id: 'real-estate-report-window',
                  visible: true
                })
              }
            }, 500)
          })
        } else {
          console.warn('[AutoReport] map01 is not available')
        }
      }

      // AI会話共有URLからの復元
      if (params.aiShareId) {
        // 地図の初期化を待ってから復元処理を実行
        if (map01) {
          map01.once('idle', async () => {
            try {
              // AI会話データを取得
              const response = await axios.get(`/oh3/php/aiShareSelect.php?id=${params.aiShareId}`)
              if (response.data.error) {
                console.error('[AiShare] Error:', response.data.error)
                return
              }
              const { mapState } = response.data

              // レイヤーを先に復元（OH3の標準フロー）
              if (mapState && mapState.selectedLayers && mapState.selectedLayers.length > 0) {
                // s_selectedLayersにセットしてOH3の標準復元フローを使用
                vm.s_selectedLayers = {
                  ...vm.s_selectedLayers,
                  map01: mapState.selectedLayers
                }
                requestAddLayers(vm.$store, 'map01')
              }

              // 地図の位置を復元
              if (mapState && mapState.center && map01) {
                map01.setCenter([mapState.center.lng, mapState.center.lat])
                if (mapState.zoom) {
                  map01.setZoom(mapState.zoom)
                }
              }

              // レイヤー復元完了を待ってからFWを開く
              setTimeout(() => {
                vm.$store.commit('setFloatingVisible', {
                  id: 'map-compare-window',
                  visible: true
                })
                // storeにaiShareIdをセット（MapCompareDialogが検知して会話を読み込み）
                vm.$store.state.pendingAiShareId = params.aiShareId
              }, 1000)
            } catch (error) {
              console.error('[AiShare] Fetch error:', error)
            }
          })
        }
      }

      // AI分析マルチ地物共有の復元
      if (params.aiShare) {
        try {
          const features = JSON.parse(decodeURIComponent(params.aiShare))
          if (Array.isArray(features) && features.length > 0) {
            // multiPointRanking は復元しない (Dialogを開き直す前提の一時的マーカー)
            const restored = features.filter((f: any) => f?.aiType !== 'multiPointRanking')
            if (restored.length > 0) {
              vm.$store.commit('setShareFeatures', restored)
              // 地図が準備できたら表示
              map01.once('idle', () => {
                vm.showShareFeaturesOnMap()
                vm.fitToShareFeatures()
              })
            }
          }
        } catch (e) {
          console.error('[AI分析共有] 復元エラー:', e)
        }
      }

      // AI物件調査レポート+ (mprd) の復元
      // 形式: encodeURIComponent(JSON.stringify({p:[[lng,lat],...], k:"preset", w:[1,2,3,4,5,6]}))
      if (params.mprdShare) {
        try {
          const data = JSON.parse(decodeURIComponent(params.mprdShare))
          if (data && Array.isArray(data.p) && data.p.length > 0) {
            const initialPoints = data.p
              .filter((pair: any) => Array.isArray(pair) && pair.length === 2)
              .map((pair: any) => ({ lng: Number(pair[0]), lat: Number(pair[1]) }))
              .filter((p: any) => !isNaN(p.lng) && !isNaN(p.lat))
            const initialPresetKey = typeof data.k === 'string' ? data.k : 'balanced'
            const initialWeights = Array.isArray(data.w) && data.w.length === 6
              ? data.w.map((n: any) => Number(n))
              : null
            if (initialPoints.length > 0) {
              // map01 の load 完了を待ってから Dialog を開く (Dialog 内で flyTo/fitBounds するため)
              map01.once('idle', () => {
                vm.openMultiPointRanking({
                  initialPoints,
                  initialPresetKey,
                  initialWeights
                })
              })
            }
          }
        } catch (e) {
          console.error('[AI物件調査レポート+] 復元エラー:', e)
        }
      }
    }

    // map02からのドローイベントをmap01に転送するプロキシ

    if (mapName === 'map02') {

      const map01 = vm.$store.state.map01
      const map02 = map // このloadハンドラ内のmapはmap02

      if (map01 && map02) {
        const drawEventTypes = ['mousedown', 'mouseup', 'click', 'dblclick', 'mousemove']

        const isDrawModeActive = () => {
          const s = vm.$store.state
          return s.isDrawLine || s.isDrawCircle || s.isDrawPolygon ||
                 s.isDrawPoint || s.editEnabled || s.isDrawLasso
        }

        drawEventTypes.forEach(eventType => {
          map02.on(eventType, (e) => {
            // clickイベントは常に転送（地物選択のため）
            // 他のイベントは描画モード時のみ転送
            // ただしmap02のドローが非表示の場合はclick転送をスキップ
            const drawHidden = !vm.$store.state.drawVisible || !vm.$store.state.drawVisiblePerMap.map02
            if (eventType === 'click' && drawHidden) return
            // ポップアップ同期OFF時: click転送するとMapLibreのcloseOnClickで
            // map01の既存ポップアップが閉じてしまうため、描画モード時のみ転送
            if (eventType === 'click' && !store.state.isFullScreenPopup && !isDrawModeActive()) return
            const shouldForward = eventType === 'click' || isDrawModeActive()
            if (!shouldForward) return
            // 転送イベントのループ防止
            if (e._fromMap02Proxy) return


            vm.$store.state.activeDrawMap = 'map02'

            // map01上の対応するピクセル座標を計算
            const point = map01.project(e.lngLat)

            // MapLibreイベントオブジェクトとして必要なメソッドをすべて含める
            const origEvent = e.originalEvent || {}
            const forwardedEvent = {
              lngLat: { lng: e.lngLat.lng, lat: e.lngLat.lat },
              point: point,
              originalEvent: origEvent,
              type: eventType,
              target: map01,
              _fromMap02Proxy: true,
              // MapLibreイベントに必要なメソッド
              preventDefault: () => {
                if (origEvent.preventDefault) origEvent.preventDefault()
              },
              stopPropagation: () => {
                if (origEvent.stopPropagation) origEvent.stopPropagation()
              },
              defaultPrevented: false
            }
            map01.fire(eventType, forwardedEvent)
          })
        })

      }
    }

    map.setProjection({ type: 'globe' })
    map.resize()
    map.doubleClickZoom.disable()

    // SDF矢印アイコンをCanvas生成（高解像度でスムーズに）
    const arrowCanvas = document.createElement('canvas')
    const arrowSize = 64 // 高解像度でスムーズな縁に
    arrowCanvas.width = arrowSize
    arrowCanvas.height = arrowSize
    const ctx = arrowCanvas.getContext('2d', { willReadFrequently: true })
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    // 白い矢印を描画（SDFは白で描画し、icon-colorで着色）
    ctx.fillStyle = '#ffffff'
    ctx.beginPath()
    // 右向き矢印（三角形）
    ctx.moveTo(arrowSize * 0.9, arrowSize * 0.5)  // 右端中央（先端）
    ctx.lineTo(arrowSize * 0.1, arrowSize * 0.15) // 左上
    ctx.lineTo(arrowSize * 0.1, arrowSize * 0.85) // 左下
    ctx.closePath()
    ctx.fill()
    const arrowImageData = ctx.getImageData(0, 0, arrowSize, arrowSize)
    map.addImage('arrow-sdf', arrowImageData, { sdf: true })

    // スタイル変更時にイメージが失われた場合の自動復元
    map.on('styleimagemissing', (e) => {
      if (e.id === 'arrow-sdf' && !map.hasImage('arrow-sdf')) {
        const canvas = document.createElement('canvas')
        const size = 64 // 高解像度でスムーズな縁に
        canvas.width = size
        canvas.height = size
        const c = canvas.getContext('2d', { willReadFrequently: true })
        c.imageSmoothingEnabled = true
        c.imageSmoothingQuality = 'high'
        c.fillStyle = '#ffffff'
        c.beginPath()
        c.moveTo(size * 0.9, size * 0.5)
        c.lineTo(size * 0.1, size * 0.15)
        c.lineTo(size * 0.1, size * 0.85)
        c.closePath()
        c.fill()
        map.addImage('arrow-sdf', c.getImageData(0, 0, size, size), { sdf: true })
      }
    })

    const dragIcon = await map.loadImage('./img/drag.png')
    map.addImage('drag-icon', dragIcon.data);

    // 自然災害伝承碑アイコン（災害種別ごとの色分け）
    const denshoVariants = [
      'densho-jishin', 'densho-tsunami', 'densho-dosya',
      'densho-suigai', 'densho-kasai', 'densho-kazan',
      'densho-takashio', 'densho-default',
    ]
    for (const name of denshoVariants) {
      const img = await map.loadImage(`./img/${name}.png`)
      map.addImage(name, img.data)
    }

    // 指定緊急避難場所・指定避難所アイコン
    const skhbImg = await map.loadImage('./img/skhb.png')
    map.addImage('skhb-icon', skhbImg.data)
    const hinanImg = await map.loadImage('./img/hinan.png')
    map.addImage('hinan-icon', hinanImg.data)

    // 沈船アイコン（SDF: 傾いた船シルエット + 波）
    {
      const S = 64
      const c = document.createElement('canvas')
      c.width = S; c.height = S
      const g = c.getContext('2d', { willReadFrequently: true })!
      g.fillStyle = '#fff'
      const u = S / 48 // 48グリッド

      // 波線（下部）
      g.lineWidth = 2.5 * u
      g.strokeStyle = '#fff'
      g.beginPath()
      g.moveTo(4 * u, 34 * u)
      g.bezierCurveTo(10 * u, 30 * u, 14 * u, 38 * u, 20 * u, 34 * u)
      g.bezierCurveTo(26 * u, 30 * u, 30 * u, 38 * u, 36 * u, 34 * u)
      g.bezierCurveTo(42 * u, 30 * u, 44 * u, 36 * u, 46 * u, 34 * u)
      g.stroke()

      // 船体（右に傾いている = 沈みかけ）
      g.save()
      g.translate(24 * u, 26 * u)
      g.rotate(0.3) // 約17度傾斜

      // 船底（台形）
      g.beginPath()
      g.moveTo(-14 * u, 2 * u)
      g.lineTo(14 * u, 2 * u)
      g.lineTo(10 * u, 8 * u)
      g.lineTo(-10 * u, 8 * u)
      g.closePath()
      g.fill()

      // マスト
      g.fillRect(-1 * u, -16 * u, 2 * u, 18 * u)

      // ×印（壊れた印）
      g.lineWidth = 2 * u
      g.strokeStyle = '#fff'
      g.beginPath()
      g.moveTo(-5 * u, -10 * u)
      g.lineTo(5 * u, -2 * u)
      g.moveTo(5 * u, -10 * u)
      g.lineTo(-5 * u, -2 * u)
      g.stroke()

      g.restore()

      const wreckImageData = g.getImageData(0, 0, S, S)
      map.addImage('wreck-ship', wreckImageData, { sdf: true })

      map.on('styleimagemissing', (e: { id: string }) => {
        if (e.id === 'wreck-ship' && !map.hasImage('wreck-ship')) {
          const c2 = document.createElement('canvas')
          c2.width = S; c2.height = S
          const g2 = c2.getContext('2d', { willReadFrequently: true })!
          // 同じ描画を再実行（styleimagemissing復元用）
          g2.fillStyle = '#fff'; g2.lineWidth = 2.5 * u; g2.strokeStyle = '#fff'
          g2.beginPath()
          g2.moveTo(4*u,34*u); g2.bezierCurveTo(10*u,30*u,14*u,38*u,20*u,34*u)
          g2.bezierCurveTo(26*u,30*u,30*u,38*u,36*u,34*u)
          g2.bezierCurveTo(42*u,30*u,44*u,36*u,46*u,34*u); g2.stroke()
          g2.save(); g2.translate(24*u,26*u); g2.rotate(0.3)
          g2.beginPath(); g2.moveTo(-14*u,2*u); g2.lineTo(14*u,2*u)
          g2.lineTo(10*u,8*u); g2.lineTo(-10*u,8*u); g2.closePath(); g2.fill()
          g2.fillRect(-1*u,-16*u,2*u,18*u)
          g2.lineWidth=2*u; g2.beginPath()
          g2.moveTo(-5*u,-10*u); g2.lineTo(5*u,-2*u)
          g2.moveTo(5*u,-10*u); g2.lineTo(-5*u,-2*u); g2.stroke()
          g2.restore()
          map.addImage('wreck-ship', g2.getImageData(0, 0, S, S), { sdf: true })
        }
      })
    }

    if (params.pitch01 || params.pitch01 === 0) {
      vm.$store.state.map01Pitch = params.pitch01
    }
    if (params.pitch02 || params.pitch02 === 0) {
      vm.$store.state.map02Pitch = params.pitch02
    }
    if (params.bearing) {
      vm.$store.state.map01Bearing = params.bearing
    }

    // ピッチが0より大きい場合は3Dコントローラーを自動で開く
    if (params.pitch01 && params.pitch01 > 0) {
      vm.$nextTick(() => {
        vm.terrainBtnExpand()
      })
    }

    if (params.simaTextForUser) {
      vm.$store.state.simaTextForUser = params.simaTextForUser
    }

    if (params.hillshade) {
      try {
        vm.$store.state.hillshade = JSON.parse(params.hillshade)
      }catch (e) {
      }
    }

    if (params.isDrawFit) {
      vm.$store.state.isDrawFit = JSON.parse(params.isDrawFit)
    }
    if (params.isFromIframe) {
      vm.$store.state.isFromIframe = JSON.parse(params.isFromIframe)
    }
    if (params.isWindow) {
      vm.$store.state.isWindow2 = JSON.parse(params.isWindow)
    }
    if (params.vector) {
      vm.$store.state.uploadedVector = JSON.parse(params.vector)
    }

    if (params.clickCircleGeojsonText && params.clickCircleGeojsonText !== 'undefined' && !params.geojsonId) {
      // 有効なJSONかどうかを先にチェック
      const geojsonText = params.clickCircleGeojsonText

      if (!geojsonText || typeof geojsonText !== 'string' || !geojsonText.trim() || !geojsonText.trim().startsWith('{')) {

        // 無効なデータはスキップ
      } else {

      vm.$store.state.clickCircleGeojsonText = geojsonText
      if (!vm.$store.state.isUsingServerGeojson) {
        featureCollectionAdd()
        markerAddAndRemove(true)
      }
      try {
        const parsedGeojson = JSON.parse(geojsonText);
        // 復帰時にlassoSelectedプロパティを強制的に削除 + offsetValue配列化
        parsedGeojson.features.forEach(f => {
          if (f.properties && f.properties.lassoSelected) {
            delete f.properties.lassoSelected
          }
          // offsetValueが配列でないとMapLibreのlookupSymbolFeaturesが壊れる
          if (f.properties && !Array.isArray(f.properties?.offsetValue)) {
            f.properties.offsetValue = [0, 0]
          }
        })
        vm.$store.state.clickCircleGeojsonText = JSON.stringify(parsedGeojson)
        vm.$store.state.lassoGeojson = JSON.stringify({ type: 'FeatureCollection', features: [] })
        const config = parsedGeojson.features.find(f => f.properties.id === 'config')?.properties
        if (config) {
          vm.$store.state.printTitleText = config['title-text']
          // マップ別タイトル復元
          vm.$store.state.printTitleTexts.map01 = config['title-text-map01'] || config['title-text'] || ''
          vm.$store.state.printTitleTexts.map02 = config['title-text-map02'] || ''
          vm.$store.state.printTitleTexts.map03 = config['title-text-map03'] || ''
          vm.$store.state.printTitleTexts.map04 = config['title-text-map04'] || ''
          vm.$store.state.textPx = config['font-size'] || 30
          vm.$store.state.titleColor = config['fill-color']
          vm.$store.state.titleDirection = config['direction'] || 'a4-vertical'
          vm.$store.state.drawVisible = config['visible'] !== false  // undefined時はtrue
          vm.$store.state.drawOpacity = config['opacity'] ?? 1  // 0を許可
          vm.$store.state.printDate = config['print-date'] || ''
        }
        // マップソースにデータをセット（復帰時に地物を表示するために必須）

        if (map && map.getSource('click-circle-source')) {
          map.getSource('click-circle-source').setData(parsedGeojson);
          // 矢印データも生成してセット
          generateStartEndPointsFromGeoJSON(parsedGeojson);
          // 距離計測ラベルも生成してセット
          generateSegmentLabelGeoJSON(parsedGeojson);
          // スタンプマーカーを復元
          vm.$nextTick(() => {
            vm.restoreStampsFromGeojson()
          })
        }
        // 写真メモ行動軌跡の矢印はmountedの最後で遅延復元する（ここでは呼ばない）
      }catch (e) {
        console.error('[JSON Parse Error] Failed to parse clickCircleGeojsonText:', e.message)
        console.error('Problematic data:', vm.$store.state.clickCircleGeojsonText?.substring(0, 200))
      }
      }
    }

    // 断面図/3Dジオラマ復元
    // 3D復元時はオーバーレイで通常マップを隠す
    if (params.elev3d === 'cs' || params.elev3d === 'dio') {
      vm.$store.state.restoring3DOverlay = true
    }
    // 3Dカメラ状態を復元（3Dコンポーネントのinit()で読み取り）
    if (params.elev3dcam) {
      const vals = params.elev3dcam.split(',').map(Number)
      if (vals.length >= 8 && vals.every(v => !isNaN(v))) {
        vm.$store.state._3dCameraState = {
          px: vals[0], py: vals[1], pz: vals[2],
          tx: vals[3], ty: vals[4], tz: vals[5],
          exag: vals[6], mini: vals[7],
          lod: vals[8] ?? 1, tex: vals[9] ?? 1
        }
      }
    }
    if (params.elevGeojsonRaw && params.elevGeojsonRaw !== 'undefined') {
      try {
        const elevGeojson = JSON.parse(params.elevGeojsonRaw)
        if (elevGeojson?.features?.length > 0) {
          vm.$store.state.elevationDrawMode = params.elevMode || 'line'
          vm.$store.state.elevationGeojson = elevGeojson
          vm.$store.commit('setFloatingVisible', { id: 'elevation-dialog', visible: true })
          if (params.elev3d === 'cs' || params.elev3d === 'dio') {
            vm.$store.state._suppressElevation3DAutoOpen = true
            // ジオラマDEMソースの復元
            if (params.elev3d === 'dio' && params.diodem === 'shimane') {
              vm.$store.state.dioramaDemSource = 'shimane'
            }
            map.once('idle', () => {
              setTimeout(() => {
                if (params.elev3d === 'cs') vm.$store.state.isCrossSection3D = true
                else if (params.elev3d === 'dio') vm.$store.state.isDiorama3D = true
                vm.$store.state._suppressElevation3DAutoOpen = false
              }, 800)
            })
          }
        }
      } catch (e) {
        console.error('[ElevationRestore] Failed to parse elevation geojson:', e)
      }
    }
    // 右下ボタンからの3Dジオラマ（elevationGeojsonなし）
    if (params.elev3d === 'dio' && (!params.elevGeojsonRaw || params.elevGeojsonRaw === 'undefined')) {
      // ジオラマDEMソースの復元
      if (params.diodem === 'shimane') {
        vm.$store.state.dioramaDemSource = 'shimane'
      }
      map.once('idle', () => {
        setTimeout(() => {
          vm.$store.state.isDiorama3D = true
        }, 800)
      })
    }

    if (params.geojsonId) {
      // まずドロー情報を取得してグループ共有ドローかどうか確認
      const formData = new FormData();
      formData.append('geojson_id', params.geojsonId);
      const response = await fetch('/oh3/php/geojson_features_with_master.php', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (data.success) {
        if (data.rows.length === 0) {
          // データが空の場合はスキップ

        } else {
          // グループ共有ドローはURLパラメータからロードしない（グループチャットからのみ）
          const geojsonName = data.rows[0].geojson_name || ''
          if (geojsonName.startsWith('グループ共有_')) {

          } else {
            // 通常のマイドローはロードを続行
            vm.$store.state.geojsonId = params.geojsonId || ''
            vm.$store.state.isUsingServerGeojson = true
            const configRow = data.rows.find(row => row.feature_id === 'config')
            vm.$store.state.isMine = data.rows[0].creator_user_id === vm.$store.state.userId
            const dbEditable = data.rows[0].is_editable
            vm.$store.state.isEditable = dbEditable === '1'
            vm.$store.state.isPrivateDraw = dbEditable === '2'
            vm.$store.state.isEditableForVSelect = dbEditable === '1' ? 'public' : dbEditable === '2' ? 'private' : 'private-edit'
            vm.$store.state.geojsonName = geojsonName
            if (configRow) {
              const config = JSON.parse(configRow.feature).properties
              vm.$store.state.printTitleText = config['title-text'] || ''
              // マップ別タイトル復元
              vm.$store.state.printTitleTexts.map01 = config['title-text-map01'] || config['title-text'] || ''
              vm.$store.state.printTitleTexts.map02 = config['title-text-map02'] || ''
              vm.$store.state.printTitleTexts.map03 = config['title-text-map03'] || ''
              vm.$store.state.printTitleTexts.map04 = config['title-text-map04'] || ''
              vm.$store.state.textPx = config['font-size'] || 30
              vm.$store.state.titleColor = config['fill-color'] || 'black'
              vm.$store.state.titleDirection = config['direction'] || 'a4-vertical'
              vm.$store.state.drawVisible = config['visible'] !== false  // undefined時はtrue
              vm.$store.state.drawOpacity = config['opacity'] ?? 1  // 0を許可
              vm.$store.state.printDate = config['print-date'] || ''
            }
            // startPollingの完了を待ってからドローデータを地図に表示（ドローモードにはしない）
            startPolling().then(() => {
              vm.$store.state.suppressDrawPitchReset = true
              // ドローデータを描画するが通常モードのまま
              vm.$nextTick(() => {
                vm.$store.state.suppressDrawPitchReset = false
                // スタンプマーカーを復元
                vm.restoreStampsFromGeojson()
              })
            })
          }
        }
      } else {
      }
    }

    // マイドロー以外の場合もドロー地物があれば自動でドローモードを開く
    if (!params.geojsonId) {
      vm.autoOpenDrawModeIfNeeded()
    }

    if (params.clickGeojsonText) {
      vm.$store.state.clickGeojsonText = params.clickGeojsonText
    }

    // if (params.drawGeojsonText) {
    //   this.$store.state.drawGeojsonText = params.drawGeojsonText
    //   const features = JSON.parse(this.$store.state.drawGeojsonText);
    //   drawInstance.addFeatures(features);
    //   this.drawControl.recalc()
    // }

    if (params.gpxText) {
      vm.$store.state.gpxText = params.gpxText
    }

    if (params.dxfText) {
      vm.$store.state.dxfText = JSON.parse(params.dxfText)
    }

    if (params.geojsonText) {
      vm.$store.state.geojsonText = params.geojsonText
    }

    if (params.kmlText) {
      vm.$store.state.kmlText = params.kmlText
    }

    try {
      vm.$store.state.extLayer = JSON.parse(params.extLayer).layer
      vm.$store.state.extLayerName = JSON.parse(params.extLayer).name
    } catch (e) {
    }

    if (params.image) {
      vm.$store.state.uploadedImage = JSON.parse(params.image)
    }

    if (params.simaText) {
      vm.$store.state.simaText = params.simaText
    }

    if (params.chibans) {
      JSON.parse(params.chibans).forEach(c => {
        vm.$store.state.highlightedChibans.add(c)
      })
    }

    if (params.simas) {
      JSON.parse(params.simas).forEach(c => {
        vm.$store.state.highlightedSimas.add(c)
      })
    }


    if (params.slj) {
      const mapNames = ['map01', 'map02', 'map03', 'map04']
      mapNames.forEach(mapName => {
        if (!params.slj[mapName]) return // map03/map04がない場合はスキップ
        params.slj[mapName].forEach(slj => {
          // if (slj.id === 'oh-amx-a-fude') {
          //   store.dispatch('triggerSnackbarForGroup', {message: '2024登記所地図は終了しました。2025版をご覧ください。'});
          // }
          const layerNames = []
          let count = 0;
          // レイヤーを探索して必要な情報を取得する関数
          function traverseLayers(layers, slj) {
            layers.forEach(layer => {
              if (layer.nodes) {
                // 子ノードがある場合は再帰的に処理
                layer.nodes.forEach(node => {
                  if (node.nodes) {
                    traverseLayers([node], slj); // 再帰処理
                  } else {
                    // 子ノードがない場合の処理
                    if (node.id === slj.id) {
                      slj.label = node.label
                      slj.source = node.source
                      slj.sources = node.sources
                      slj.layers = node.layers
                      slj.attribution = node.attribution
                      slj.info = node.info
                    }
                    layerNames.push(node.label)
                    count++
                  }
                });
              } else {
                // 子ノードがない場合の処理
                // if (slj.id === 'oh-amx-a-fude') {
                //   const result = toRaw(layers).find(layer => {
                //     return layer.id === 'oh-homusyo-2025-layer'
                //   })
                //   slj.id = result.id
                //   slj.label = result.label
                //   slj.source = result.source
                //   slj.sources = result.sources
                //   slj.layers = result.layers
                //   slj.attribution = result.attribution
                //   slj.ext = result.ext
                //   slj.info = result.info
                // } else {
                //   if (layer.id === slj.id) {
                //     slj.label = layer.label
                //     slj.source = layer.source
                //     slj.sources = layer.sources
                //     slj.layers = layer.layers
                //     slj.attribution = layer.attribution
                //     slj.info = layer.info
                //   }
                // }
                if (layer.id === slj.id) {
                  slj.label = layer.label
                  slj.source = layer.source
                  slj.sources = layer.sources
                  slj.layers = layer.layers
                  slj.attribution = layer.attribution
                  slj.info = layer.info
                }
                layerNames.push(layer.label)
                count++;
              }
            });
          }
          // レイヤーの探索を開始
          // map03/map04はLayers.layersに定義がないため、map01のレイヤー定義を使用
          const layerKey = (mapName === 'map03' || mapName === 'map04') ? 'map01' : mapName
          const layers = Layers.layers[layerKey];
          if (!layers) return // レイヤー定義がない場合はスキップ
          traverseLayers(layers, slj);
          // 必要であれば処理結果のログを出力
          const uniqueLayerNames = [...new Set(layerNames)]
        });

        // ブレンドレイヤーの復元処理
        params.slj[mapName].forEach(slj => {
          if (slj.id && slj.id.startsWith('oh-blend-') && slj.blendConfig) {
            const config = slj.blendConfig
            const blendSourceId = `oh-blend-source-${slj.id.split('-')[2]}`

            slj.ext = {
              name: 'extBlend',
              autoOpenDialog: false
            }

            // attributionがない場合はデフォルト値を設定
            if (!slj.attribution) {
              slj.attribution = `ブレンドモード: ${config.mode}`
            }

            slj.source = {
              id: blendSourceId,
              obj: {
                type: 'raster',
                tiles: [
                  `blend://${config.mode}/{z}/{x}/{y}.png?base=${encodeURIComponent(config.baseTiles)}&overlay=${encodeURIComponent(config.overlayTiles)}`
                ],
                tileSize: config.tileSize || 256
              }
            }

            slj.layers = [
              {
                id: slj.id,
                type: 'raster',
                source: blendSourceId,
                paint: {
                  'raster-opacity': 1
                }
              }
            ]
          }
        });

        const result = params.slj[mapName].find(v => v.id === 'oh-extLayer')
        if (result) {
          extSource.obj.tiles = [vm.$store.state.extLayer]
          result.sources = [extSource]
          result.layers = [extLayer]
          result.label = vm.$store.state.extLayerName
        }
      });
    } else {
      // パラメータなしの場合は両方のマップにOSMベクターを追加（一度だけ）
      if (!vm.osmVectorInitialized) {
        const mapNames = ['map01', 'map02']
        mapNames.forEach(mapName => {
          if (
              location.hostname === 'localhost' ||      // ローカル
              location.hostname === '127.0.0.1' ||      // ローカルIP
              location.hostname.startsWith('192.168.')  // プライベートIP
          ) {
            // ローカル環境の処理
            vm.s_selectedLayers[mapName].unshift(
                {
                  id: 'oh-vector-layer-osm-bright',
                  label: 'OSMベクター',
                  sources: osmBrightSources,
                  layers: osmBrightLayers,
                  opacity: 1,
                  visibility: true,
                  attribution: '© <a href="https://wiki.openstreetmap.org/wiki/Japan/OSMFJ_Tileserver" target="_blank">OpenStreetMap</a> contributors',
                  credit: '© OpenStreetMap contributors',
                }
            )
          } else {
            // 本番環境の処理
            vm.s_selectedLayers[mapName].unshift(
                {
                  id: 'oh-vector-layer-osm-bright',
                  label: 'OSMベクター',
                  sources: osmBrightSources,
                  layers: osmBrightLayers,
                  opacity: 1,
                  visibility: true,
                  attribution: '© <a href="https://wiki.openstreetmap.org/wiki/Japan/OSMFJ_Tileserver" target="_blank">OpenStreetMap</a> contributors',
                  credit: '© OpenStreetMap contributors',
                }
            )
          }
        })
        vm.osmVectorInitialized = true
      }
    }
    // -----------------------------------------------------------------------------------------------------------
    let fetchFlg = false;
    async function processUserLayers(slj,mapName) {
      const promises = slj.map(async (v) => {
        if (v.id.includes('usertile')) {
          fetchFlg = true;
          const layerId = v.id.split('-')[2];
          try {
            const response = await axios.get('/oh3/php/userTileSelectById.php', {
              params: { id: layerId }
            });
            if (response.data.error) {
              console.error('エラー:', response.data.error);
              alert(`エラー: ${response.data.error}`);
            } else {
              let url = response.data[0].url
              let source
              if (url.includes('{-y}')) {
                url = url.replace(/{-y}/,'{y}')
                source = {
                  id: response.data[0].name + '-source',obj: {
                    type: 'raster',
                    tiles: [url],
                    scheme: 'tms'
                  }
                }
              } else {
                source = {
                  id: response.data[0].name + '-source',obj: {
                    type: 'raster',
                    tiles: [url]
                  }
                }
              }
              // source = {
              //   id: response.data[0].name + '-source',
              //   obj: { type: 'raster', tiles: [response.data[0].url] }
              // };
              const layer = {
                id: 'oh-' + response.data[0].name + '-layer',
                type: 'raster',
                source: response.data[0].name + '-source'
              };

              v.sources = [source];
              v.layers = [layer];
              v.label = response.data[0].name;
            }
          } catch (error) {
            console.error('フェッチエラー:', error);
          }
        }

        if (v.id.includes('oh-sima-')) {
          fetchFlg = true;
          const layerId = v.id.split('-')[2];
          try {
            const response = await axios.get('/oh3/php/userSimaSelectById.php', {
              params: { id: layerId }
            });
            if (response.data.error) {
              console.error('エラー:', response.data.error);
              // アラートを削除（起動時の煩わしいエラー表示を防ぐ）
            } else {
              const name = response.data[0].name;
              const id = response.data[0].id;
              const zahyokei = response.data[0].zahyokei;
              const simaText = response.data[0].simatext;
              async function aaa() {
                const sourceAndLayers = await userSimaSet(name, '', id, zahyokei, simaText)
                store.state.geojsonSources.push({
                  sourceId: sourceAndLayers.source.id,
                  source: sourceAndLayers.source
                })
                v.source = sourceAndLayers.source.id;
                v.layers = sourceAndLayers.layers;
                v.label = name;
                // vm.$store.state.simaTextForUser = JSON.stringify({
                //   text: simaText,
                //   opacity:JSON.parse(vm.$store.state.simaTextForUser).opacity
                // })
                // vm.s_simaOpacity = JSON.parse(vm.$store.state.simaTextForUser).opacity
                if (mapName === 'map01') {
                  vm.$store.state.snackbar = true
                  vm.$store.state.simaText = ''
                }
              }
              await aaa()
            }
          } catch (error) {
            vm.$store.state.snackbar = false
            console.error('フェッチエラー:', error);
          }
        }

        if (v.id.includes('oh-dxf-')) {
          fetchFlg = true;
          const layerId = v.id.split('-')[2];
          try {
            const response = await axios.get('/oh3/php/userDxf.php', {
              params: { action: 'selectById', id: layerId }
            });
            if (response.data.error) {
              console.error('DXF復帰エラー:', response.data.error);
            } else if (response.data.result) {
              const name = response.data.result.name;
              const id = response.data.result.id;
              const dxfText = response.data.result.dxftext;
              const zahyokei = response.data.result.zahyokei;

              if (!zahyokei) {
                console.error('DXF復帰エラー: 座標系情報がありません (id:', id, 'name:', name, ')');
                return;
              }

              async function restoreDxf() {
                const sourceAndLayers = await userDxfSet(name, '', id, dxfText, true, zahyokei)
                if (!sourceAndLayers) return

                store.state.geojsonSources.push({
                  sourceId: sourceAndLayers.source.id,
                  source: sourceAndLayers.source
                })

                v.source = sourceAndLayers.source.id;
                v.layers = sourceAndLayers.layers;
                v.label = name;

                if (mapName === 'map01') {
                  vm.$store.state.snackbar = true
                }
              }
              await restoreDxf()
            }
          } catch (error) {
            vm.$store.state.snackbar = false
            console.error('DXFフェッチエラー:', error);
          }
        }

        // 謄本オーバーレイの復元
        if (v.id.includes('oh-touhon-')) {
          fetchFlg = true;
          const touhonId = v.id.split('-')[2];
          try {
            const response = await axios.get('/oh3/php/userTouhon0SelectById.php', {
              params: { id: touhonId }
            });
            if (response.data.error) {
              console.error('謄本復帰エラー:', response.data.error);
            } else if (response.data && response.data.length > 0) {
              const item = response.data[0];
              const sourceId = 'oh-touhon-' + item.id + '-source';
              const layerId = 'oh-touhon-' + item.id + '-layer';

              // cornersまたはboundsから座標を取得
              let coordinates;
              const bounds = JSON.parse(item.bounds);
              if (item.corners) {
                const corners = typeof item.corners === 'string' ? JSON.parse(item.corners) : item.corners;
                coordinates = [
                  [corners.topLeft.lng, corners.topLeft.lat],
                  [corners.topRight.lng, corners.topRight.lat],
                  [corners.bottomRight.lng, corners.bottomRight.lat],
                  [corners.bottomLeft.lng, corners.bottomLeft.lat],
                ];
              } else {
                coordinates = [
                  [bounds[0], bounds[3]],
                  [bounds[2], bounds[3]],
                  [bounds[2], bounds[1]],
                  [bounds[0], bounds[1]],
                ];
              }

              // ソースを作成
              const source = {
                id: sourceId,
                obj: {
                  type: 'image',
                  url: item.url,
                  coordinates: coordinates
                }
              };

              // レイヤーを作成
              const layer = {
                id: layerId,
                type: 'raster',
                source: sourceId,
                paint: {
                  'raster-opacity': (item.opacity || 80) / 100
                }
              };

              v.sources = [source];
              v.layers = [layer];
              v.label = item.name;
              v.opacity = (item.opacity || 80) / 100;
              v.type = 'touhon';
            }
          } catch (error) {
            console.error('謄本フェッチエラー:', error);
          }
        }

        // 鮮明化画像の復元
        if (v.id.includes('oh-senmeika-')) {
          fetchFlg = true;
          const senmeikaId = v.id.split('-')[2];
          try {
            const response = await axios.get('/oh3/php/senmeika-list.php', {
              params: { id: senmeikaId }
            });
            if (response.data.success && response.data.data.length > 0) {
              const item = response.data.data[0];
              const bounds = Array.isArray(item.bounds) ? item.bounds : JSON.parse(item.bounds);
              // bounds = [west, south, east, north]
              const coordinates = [
                [bounds[0], bounds[3]], // top-left
                [bounds[2], bounds[3]], // top-right
                [bounds[2], bounds[1]], // bottom-right
                [bounds[0], bounds[1]]  // bottom-left
              ];
              const sourceId = 'oh-senmeika-' + item.id + '-source';
              const layerId = 'oh-senmeika-' + item.id + '-layer';

              v.sources = [{
                id: sourceId,
                obj: { type: 'image', url: item.url, coordinates }
              }];
              v.layers = [{
                id: layerId,
                type: 'raster',
                source: sourceId,
                paint: { 'raster-opacity': (item.opacity || 100) / 100 }
              }];
              v.label = item.name;
              v.opacity = 1;
            }
          } catch (error) {
            console.error('鮮明化画像復元エラー:', error);
          }
        }

        if (v.id.includes('oh-kmz-')) {
          fetchFlg = true;
          const layerId = v.id.split('-')[2];
          try {
            const response = await axios.get('/oh3/php/userKmzSelectById.php', {
              params: { id: layerId }
            });
            if (response.data.error) {
              console.error('エラー:', response.data.error);
              alert(`エラー: ${response.data.error}`);
            } else {
              const name = response.data[0].name;
              const id = response.data[0].id;
              const url = response.data[0].url;
              async function aaa() {
                const sourceAndLayers = await userKmzSet(name, url, id)
                store.state.geojsonSources.push({
                  sourceId: sourceAndLayers.source.id,
                  source: sourceAndLayers.source
                })
                v.source = sourceAndLayers.source.id;
                v.layers = sourceAndLayers.layers;
                v.label = name;
              }
              await aaa()
            }
          } catch (error) {
            console.error('フェッチエラー:', error);
          }
        }

        if (v.id.includes('oh-kml-')) {
          fetchFlg = true;
          const layerId = v.id.split('-')[2];
          try {
            const response = await axios.get('/oh3/php/userKmzSelectById.php', {
              params: { id: layerId }
            });
            if (response.data.error) {
              console.error('エラー:', response.data.error);
              alert(`エラー: ${response.data.error}`);
            } else {
              const name = response.data[0].name;
              const id = response.data[0].id;
              const url = response.data[0].url;
              async function aaa() {
                const sourceAndLayers = await userKmlSet(name, url, id)
                if (!sourceAndLayers) {
                  console.error('Failed to load KML file:', name)
                  return
                }
                store.state.geojsonSources.push({
                  sourceId: sourceAndLayers.source.id,
                  source: sourceAndLayers.source
                })
                v.source = sourceAndLayers.source.id;
                v.layers = sourceAndLayers.layers;
                v.label = name;
              }
              await aaa()
            }
          } catch (error) {
            console.error('KMLフェッチエラー:', error);
          }
        }

        if (v.id.includes('oh-vpstile-')) {
          fetchFlg = true;
          const layerId = v.id.split('-')[2];
          try {
            const response = await axios.get('/oh3/php/userXyztileSelectById.php', {
              params: { id: layerId }
            });
            if (response.data.error) {
              console.error('エラー:', response.data.error);
              alert(`エラー: ${response.data.error}`);
            } else {
              const name = response.data[0].name;
              const id = response.data[0].id;
              let url = response.data[0].url;
              const bbox = JSON.parse(response.data[0].bbox);
              const bounds = [bbox[0], bbox[1], bbox[2], bbox[3]];
              const transparent = JSON.parse(response.data[0].transparent);
              let tile = ''
              let source = null
              if (url.endsWith('.pmtiles')) {
                // transparentPmtiles://https://example.com/tiles.pmtiles/10/123/456
                // alert('transparentPmtiles://' + url + '/{z}/{x}/{y}')
                url = url.replace('https://kenzkenz.duckdns.org/','https://kenzkenz.net/')
                source = {
                  id: 'oh-vpstile-' + id + '-' + name + '-source',
                  obj: {
                    type: 'raster',
                    url: 'pmtiles://' + url,
                    // tiles: ['transparentPmtiles://' + url + '/{z}/{x}/{y}'],
                    bounds: bounds,
                    maxzoom: 26 }
                };
              } else {
                if (transparent !== 0) {
                  tile = 'transparentBlack://' + url
                } else {
                  tile = url
                }
                source = {
                  id: 'oh-vpstile-' + id + '-' + name + '-source',
                  obj: {
                    type: 'raster',
                    tiles: [tile],
                    bounds: bounds,
                    maxzoom: 26 }
                };
              }
              const layer = {
                id: 'oh-vpstile-' + id + '-' + name + '-layer',
                type: 'raster',
                source: 'oh-vpstile-' + id + '-' + name + '-source'
              };
              v.sources = [source];
              v.layers = [layer];
              v.label = response.data[0].name;
            }
          } catch (error) {
            console.error('フェッチエラー:', error);
          }
        }

        if (v.id.includes('oh-chiban-')) {
          fetchFlg = true
          const layerId = v.id.split('-')[2];
          try {
            const response = await axios.get('/oh3/php/userPmtilesSelectById.php', {
              params: { id: layerId }
            });
            if (response.data.error) {
              console.error('エラー:', response.data.error);
              alert(`エラー: ${response.data.error}`);
            } else {

              const name = response.data[0].name
              const id = response.data[0].id
              let url = response.data[0].url
              url = url.replace('https://kenzkenz.duckdns.org/','https://kenzkenz.net/')
              const chiban = response.data[0].chiban
              const length = response.data[0].length

              const source = {
                id: 'oh-chiban-' + id + '-' + name + '-source', obj: {
                  type: 'vector',
                  url: "pmtiles://" + url
                }
              };
              const polygonLayer = {
                id: 'oh-chiban-' + id + '-' + name + '-layer',
                type: 'fill',
                source: 'oh-chiban-' + id + '-' + name + '-source',
                "source-layer": 'oh3',
                'paint': {
                  'fill-color': 'rgba(0,0,0,0)',
                  // 'fill-color': [
                  //   'case',
                  //   ['has', 'AzaName'],
                  //   'rgba(0,0,255,0.5)',
                  //   'rgba(0,0,0,0)'
                  // ],
                },
              }
              const lineLayer = {
                id: 'oh-chibanL-' + name + '-line-layer',
                source: 'oh-chiban-' + id + '-' + name + '-source',
                type: 'line',
                "source-layer": "oh3",
                paint: {
                  'line-color': 'blue',
                  'line-width': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    1, 0.1,
                    16, 2
                  ]
                },
              }
              let minZoom
              if (!length) {
                minZoom = 17
              } else if (length < 10000) {
                minZoom = 0
              } else {
                minZoom = 17
              }
              // const labelLayer = {
              //   id: 'oh-chibanL-' + name + '-label-layer',
              //   type: "symbol",
              //   source: 'oh-chiban-' + id + '-' + name + '-source',
              //   "source-layer": "oh3",
              //   'layout': {
              //     'text-field': ['get', chiban],
              //     'text-font': ['NotoSansJP-Regular'],
              //   },
              //   'paint': {
              //     'text-color': 'navy',
              //     'text-halo-color': 'rgba(255,255,255,1)',
              //     'text-halo-width': 1.0,
              //   },
              //   'minzoom': minZoom
              // }
              const labelLayer = {
                id: 'oh-chibanL-' + name + '-label-layer',
                type: "symbol",
                source: 'oh-chiban-' + id + '-' + name + '-source',
                "source-layer": "oh3",
                layout: {
                  'text-field': ['get', chiban],
                  'text-font': ['NotoSansJP-Regular'],
                  'text-offset': [
                    'case',
                    ['==', ['geometry-type'], 'Point'], ['literal', [0, 1.2]],  // ポイントは下にずらす
                    ['literal', [0, 0]]  // ポリゴンなどはそのまま
                  ]
                },
                paint: {
                  'text-color': 'navy',
                  'text-halo-color': 'rgba(255,255,255,1)',
                  'text-halo-width': 1.0,
                },
                minzoom: minZoom
              };

              // point layer（現在は非表示）
              const pointLayer = {
                id: 'oh-chibanL-' + name + '-point-layer',
                type: "circle",
                source: 'oh-chiban-' + id + '-' + name + '-source',
                "source-layer": "oh3",
                filter: ["==", "$type", "Point"],
                layout: {
                  visibility: 'none', // 常に非表示
                },
                paint: {
                  'circle-color': 'rgba(255,0,0,1)',
                  'circle-radius': 5,
                  'circle-opacity': 1,
                  'circle-stroke-width': 1,
                  'circle-stroke-color': '#fff'
                },
              };
              // vertex layer（現在は非表示）
              const vertexLayer = {
                id: 'oh-chibanL-' + name + '-vertex-layer',
                type: "circle",
                source: 'oh-chiban-' + id + '-' + name + '-source',
                filter: ["==", "$type", "Polygon"],
                "source-layer": "oh3",
                layout: {
                  visibility: 'none', // 常に非表示
                },
                paint: {
                  'circle-radius': [
                    'interpolate', ['linear'], ['zoom'],
                    15, 0,
                    18,4
                  ],
                  'circle-color': 'rgba(0,0,0,0)',
                }
              };
              v.sources = [source];
              v.layers = [polygonLayer,lineLayer,labelLayer]
              v.label = name;
            }
          } catch (error) {
            console.error('フェッチエラー:', error);
          }
        }

        if (v.id.includes('oh-pmtiles-')) {
          fetchFlg = true
          const layerId = v.id.split('-')[2];
          try {
            const response = await axios.get('/oh3/php/userPmtiles0SelectById.php', {
              params: { id: layerId }
            });
            if (response.data.error) {
              console.error('エラー:', response.data.error);
              alert(`エラー: ${response.data.error}`);
            } else {

              const name = response.data[0].name
              const id = response.data[0].id
              const url = response.data[0].url
              // labelがnullまたは空の場合はデフォルトで'label'プロパティを使用
              const label = response.data[0].label || 'label'
              const length = response.data[0].length

              const source = {
                id: 'oh-pmtiles-' + id + '-source', obj: {
                  type: 'vector',
                  url: "pmtiles://" + url
                }
              };
              const polygonLayer = {
                id: 'oh-pmtiles-' + id + '-layer',
                type: 'fill',
                source: 'oh-pmtiles-' + id + '-source',
                "source-layer": 'oh3',
                'paint': {
                  'fill-color': 'rgba(0,0,0,0)',
                },
              }
              const lineLayer = {
                id: 'oh-pmtiles-' + id + '-line-layer',
                source: 'oh-pmtiles-' + id + '-source',
                type: 'line',
                "source-layer": "oh3",
                paint: {
                  'line-color': 'blue',
                  'line-width': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    1, 0.1,
                    16, 2
                  ]
                },
              }
              let minZoom
              if (!length) {
                minZoom = 17
              } else if (length < 10000) {
                minZoom = 0
              } else {
                minZoom = 17
              }
              const labelLayer = {
                id: 'oh-pmtiles-' + id + '-label-layer',
                type: "symbol",
                source: 'oh-pmtiles-' + id + '-source',
                "source-layer": "oh3",
                layout: {
                  'text-field': ['get', label],
                  'text-offset': [
                    'case',
                    ['==', ['geometry-type'], 'Point'], ['literal', [0, 1.2]],  // ポイントは下にずらす
                    ['literal', [0, 0]]  // ポリゴンなどはそのまま
                  ]
                },
                paint: {
                  'text-color': 'black',
                  'text-halo-color': 'rgba(255,255,255,1)',
                  'text-halo-width': 1.0,
                },
              };

              const pointLayer = {
                id: 'oh-pmtiles-' + id + '-point-layer',
                type: "circle",
                source: 'oh-pmtiles-' + id + '-source',
                "source-layer": "oh3",
                filter: ["==", "$type", "Point"],
                paint: {
                  'circle-color': 'rgba(0,0,0,1)',
                  'circle-radius': 8,
                  'circle-opacity': 1,
                  'circle-stroke-width': 1,
                  'circle-stroke-color': '#fff'
                },
              };
              const vertexLayer = {
                id: 'oh-pmtiles-' + id + '-vertex-layer',
                type: "circle",
                source: 'oh-pmtiles-' + id + '-source',
                filter: ["==", "$type", "Polygon"],
                "source-layer": "oh3",
                paint: {
                  'circle-radius': [
                    'interpolate', ['linear'], ['zoom'],
                    15, 0,
                    18,0
                  ],
                  'circle-color': 'red',
                }
              };
              v.sources = [source];
              v.layers = [polygonLayer,lineLayer,labelLayer]
              v.label = name;
            }
          } catch (error) {
            console.error('フェッチエラー:', error);
          }
        }

      });
      return Promise.all(promises);
    }


    // vmはinitApp()のパラメータとして渡されている

    async function fetchAllUserLayers() {
      await Promise.all([
        processUserLayers(params.slj.map01,'map01'),
        processUserLayers(params.slj.map02,'map02')
      ]);
      // s_selectedLayers設定の前に全マップのレイヤー追加をリクエスト
      requestAddLayers(vm.$store, 'map01')
      requestAddLayers(vm.$store, 'map02')
      requestAddLayers(vm.$store, 'map03')
      requestAddLayers(vm.$store, 'map04')
      // パラメータなし（DB起動時レイヤー復元）または3D復元時はextダイアログを抑制
      if (!vm.dbparams || params.elev3d === 'cs' || params.elev3d === 'dio') {
        vm.$store.state.suppressExtAutoOpen = true
      }
      vm.s_selectedLayers = params.slj;
      // 全マップのaddLayers完了後にフラグをクリア（map01/map02両方のwatcher発火を待つ）
      if (vm.$store.state.suppressExtAutoOpen) {
        setTimeout(() => {
          vm.$store.state.suppressExtAutoOpen = false
        }, 2000)
      }
    }

    // 非同期関数を実行（awaitして完了を待つ - 黒画面問題対応）
    if (params.slj) {
      await fetchAllUserLayers()
    }

    // 今昔マップレイヤーがあればタイムスライダーを自動で開く
    vm.openTimeSliderIfKonjyakuExists()

    // this.s_selectedLayers.map01 = this.s_selectedLayers.map01.filter(layer => layer.id !== 'oh-konzyaku-layer')

    // ----------------------------------------------------------------

    // const gsiTerrainSource = useGsiTerrainSource(maplibregl.addProtocol, {
    //   tileUrl: 'https://tiles.gsj.jp/tiles/elev/mixed/{z}/{y}/{x}.png',
    //   maxzoom: 17,
    //   attribution: '<a href="https://gbank.gsj.jp/seamless/elev/">産総研シームレス標高タイル</a>'
    // });
    // const gsiTerrainSource = useGsiTerrainSource(maplibregl.addProtocol);
    // map.addSource(gsiTerrainSource)




    // 標高タイルソース---------------------------------------------------
    // map.addSource("gsidem-terrain-rgb", {
    //   type: 'raster-dem',
    //   tiles: ['https://xs489works.xsrv.jp/raster-tiles/gsi/gsi-dem-terrain-rgb/{z}/{x}/{y}.png'],
    //   // tiles: ['https://mapdata.qchizu2.xyz/03_dem/51_int/all_9999/int_01/{z}/{x}/{y}.png'],
    //   attribution: '<a href="https://maps.gsi.go.jp/development/ichiran.html#dem" target="_blank">地理院タイル(標高タイル)</a>',
    //   tileSize: 256,
    // })
    // 標高タイルセット
    // map.setTerrain({ 'source': 'gsidem-terrain-rgb', 'exaggeration': this.s_terrainLevel });
    // const vm = this  // 上部で定義済み
    if (isNaN(vm.s_terrainLevel)) vm.s_terrainLevel = 1

    // document.querySelector('#' + mapName + ' .terrain-btn-up,terrain-btn-down').addEventListener('mouseover', function() {
    //   const layers = map.getStyle().layers
    //   if (!layers.find(layer => {
    //         return layer.id.indexOf('height') !== -1
    //       }
    //   )) {
    //     map.setTerrain({'source': 'terrain', 'exaggeration': vm.s_terrainLevel})
    //   } else {
    //     map.setTerrain(null)
    //   }
    // }, false);
    // 上ボタンのテレイン自動制御は廃止
    // テレインはスプレッドメニューで選択した時点で制御する






    // addEventListener('keydown', function () {
    //   map.setTerrain({ 'source': 'gsidem-terrain-rgb', 'exaggeration': vm.s_terrainLevel })
    // })
    // 標高タイルソース---------------------------------------------------
    // map.addSource("aws-terrain", {
    //   type: "raster-dem",
    //   minzoom: 1,
    //   maxzoom: 15,
    //   // このソースが使用するエンコーディング。terrarium（Terrarium形式のPNGタイル）、mapbox（Mapbox Terrain RGBタイル）、custom のいずれか
    //   encoding: "terrarium",
    //   tiles: ["https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png"],
    // })
    // // 標高タイルセット
    // map.setTerrain({ 'source': 'aws-terrain', 'exaggeration': this.s_terrainLevel });

    // Skyレイヤ
    if (mapName === 'map01') {
      map.setSky({
        "sky-color": "#199EF3",
        "sky-horizon-blend": 0.7,
        "horizon-color": "#f0f8ff",
        "horizon-fog-blend": 0.8,
        "fog-color": "#2c7fb8",
        "fog-ground-blend": 0.9,
        "atmosphere-blend": ["interpolate", ["linear"], ["zoom"], 0, 1, 12, 0]
      })
    } else {
      map.setSky({
        "sky-color": "#199EF3",
        "sky-horizon-blend": 0.71,//少し変えるとskayが反映するようになる。謎。
        "horizon-color": "#f0f8ff",
        "horizon-fog-blend": 0.8,
        "fog-color": "#2c7fb8",
        "fog-ground-blend": 0.9,
        "atmosphere-blend": ["interpolate", ["linear"], ["zoom"], 0, 1, 12, 0]
      })
    }
    // -----------------------------------------------------------------------------------------------------------
    // ここを改善
    // map.on('moveend', this.updatePermalink)
    map.on('click', vm.updatePermalink)
    /**
     * テスト的に下記のdragendをiphoneのときに無効化
     */
    if (!vm.isIphone) map.on('dragend', vm.updatePermalink)

    map.on('zoomend', vm.updatePermalink)
    // map.on('idle', this.updatePermalink)

    // デバウンス関数
    function debounce(func, delay) {
      let timer;
      return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => func.apply(this, args), delay);
      };
    }

    // マップ範囲の変更を処理
    let previousBounds = null;
    let previousZoom = map.getZoom(); // 初期ズームレベルを取得

    const handleBoundsChange = () => {
      const currentBounds = map.getBounds();
      const currentZoom = map.getZoom();

      // ズームイン時は発火しない
      if (currentZoom > previousZoom) {
        previousZoom = currentZoom;
        previousBounds = currentBounds;
        return;
      }

      if (
          !previousBounds ||
          currentBounds.getNorth() > previousBounds.getNorth() ||
          currentBounds.getSouth() < previousBounds.getSouth() ||
          currentBounds.getEast() > previousBounds.getEast() ||
          currentBounds.getWest() < previousBounds.getWest()
      ) {
        // マップの範囲が拡大または移動した場合のみ発火
        vm.$store.state.osmFire = !vm.$store.state.osmFire;
      }

      // 現在の範囲とズームレベルを保存
      previousBounds = currentBounds;
      previousZoom = currentZoom;
    };

    // デバウンス版の関数を作成（遅延を500msに設定）
    const debouncedHandleBoundsChange = debounce(handleBoundsChange, 500);

    // イベントリスナーにデバウンス関数を適用
    if (!vm.isIphone) {
      map.on('dragend', debouncedHandleBoundsChange);
      map.on('zoomend', debouncedHandleBoundsChange);
    }

    // スマートフォンのとき
    if (vm.s_isSmartPhone) {
      map.dragRotate.disable()
      map.touchZoomRotate.disableRotation()
    }

    const moveOrMoveene = vm.isIphone ? 'moveend' : 'move'

    map.on(moveOrMoveene, async () => {
      const center = map.getCenter(); // マップの中心座標を取得
      const lon = center.lng; // 経度
      const lat = center.lat; // 緯度
      // const zoom = Math.round(map.getZoom()); // 現在のズームレベル
      // 標高を取得
      const elevation = await fetchElevation(lon, lat);
      // 標高を画面に表示
      if (elevation !== null) {
        if (elevation <= 4000) {
          vm.$store.state.elevation = `中心の標高: ${elevation.toFixed(2)}m`
        } else {
          vm.$store.state.elevation = ''
        }
      } else {
        vm.$store.state.elevation = ''
      }
    });
    //------------------------------------------------------------------------------------------------------------
    map.on('mousemove', (e) => {
      try {
        const features = map.queryRenderedFeatures(e.point);
        const isOverTarget = features.some(f => f.layer.id !== 'zones-layer');
        if (isOverTarget) {
          map.getCanvas().classList.add('force-pointer');
        } else {
          map.getCanvas().classList.remove('force-pointer');
        }
      } catch (err) {
        console.warn('[mousemove] queryRenderedFeatures failed', err);
      }
    });
    //------------------------------------------------------------------------------------------------------------
    // ポップアップ
    map.on('click', (e) => {
      if (vm.$store.state.elevationDrawMode) return
      if (hitExcludedLayers(map, e.point)) return; // ← 除外レイヤーなら何もしない
      if (vm.$store.state.isDrawPoint) return
      const drawLayerIds = [
        'arrows-endpoint-label-layer',
        'click-circle-symbol-layer',
        'click-circle-line-layer',
        'click-circle-keiko-line-layer',
        'click-circle-memo-mark-layer',
        'click-circle-label-layer',
        'click-circle-layer',
        'click-circle-polygon-symbol-layer',
        'click-circle-polygon-symbol-area-layer',
        'segment-label-layer',
      ].filter((layerId) => map.getLayer(layerId))
      const drawHits = drawLayerIds.length ? map.queryRenderedFeatures(e.point, { layers: drawLayerIds }) : []
      if (drawHits.length > 0) return
      // ポップアップ同期OFF時、map02プロキシ転送によるmap01ポップアップをスキップ
      if (e._fromMap02Proxy && !store.state.isFullScreenPopup) return
      popup(e,map,mapName,vm.s_map2Flg)
      // ポップアップ同期: 他のマップにも同じ座標でポップアップを発火
      triggerFullScreenPopup(e, mapName)
      // store.commit('setDrawDrawer', false)
      const lng = e.lngLat.lng;  // 経度
      const lat = e.lngLat.lat;  // 緯度
      if (store.state.mapillaryFlg) {
        /**
         * ちょっとわかりにくいがマピラリオンのときなにもないとろこを
         * クリックしてもなにもないことを表示するために必要
         */
        store.state.mapillaryFeature = null
        mapillaryCreate(lng, lat)
      }
      // ストリートビュー: カバレッジON時に地図クリックでFW更新
      if (store.state.streetViewFlg) {
        map.getCanvas().classList.add('force-wait')
        store.state.streetViewLoading = true
        consumeToken('street_view').then(r => {
          if (!r.success && (r.error === 'POINT_INSUFFICIENT' || r.error === 'LOGIN_REQUIRED')) {
            map.getCanvas().classList.remove('force-wait')
            store.state.streetViewLoading = false
            store.state.streetViewFlg = false
            localStorage.setItem('streetView', 'false')
            store.dispatch('hideFloatingWindow', 'street-view-window')
            if (map.getLayer('street-view-coverage-layer')) {
              map.setLayoutProperty('street-view-coverage-layer', 'visibility', 'none')
            }
            return
          }
          store.state.streetViewCoords = { lat, lng, _t: Date.now() }
          store.dispatch('showFloatingWindow', 'street-view-window')
          // カーソル復元はStreetViewWindow.vueのstatus_changedで行う
        })
      }
    })
    map.on('mousemove', function (e) {
      mouseMoveForPopup(e,map)
    })

    // -----------------------------------------------------------------------------------------------------------
    // terrain パラメータによるテレイン制御
    // terrain=1 → テレインを有効化（3D表示＋地形あり）
    // terrain=0 または terrainパラメータなし → テレインを無効化
    if (params.terrain === true) {
      // ドローで高さのポリゴンがある場合はテレインを無効化
      const layers = vm.s_selectedLayers?.[mapName] || []
      const hasHeightLayer = layers.find(layer => layer.id?.indexOf('height') !== -1)
      if (!hasHeightLayer) {
        const source = map.getSource('click-circle-source')
        const sourceData = source && source._data ? (source._data.geojson ? source._data.geojson : source._data) : null
        const isHeight = sourceData && sourceData.features ? sourceData.features.find(f => f.properties?.height > 0) : null
        if (!isHeight) {
          vm.$store.state[mapName].setTerrain({'source': 'terrain', 'exaggeration': vm.s_terrainLevel})
          vm.$store.state.isTerrainEnabled = true
        }
      } else {
        map.setTerrain(null)
        vm.$store.state.isTerrainEnabled = false
      }
    } else {
      // terrain=0 または terrainパラメータなし → テレインを無効化
      map.setTerrain(null)
      vm.$store.state.isTerrainEnabled = false
    }
    // -----------------------------------------------------------------------------------------------------------
    // ドラッグ&ドロップ用の要素
    const dropzone = document.getElementById('map00');
    // ドラッグイベントのリスナー追加
    document.addEventListener('dragover', (event) => {
      event.preventDefault();
      dropzone.classList.add('active');
    });
    document.addEventListener('dragleave', (event) => {
      event.preventDefault();
      dropzone.classList.remove('active');
    });

    if (mapName === 'map01') {
      dropzone.addEventListener('drop', async(e) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (files.length === 0) return;

        // ファイルドロップをトラッキング
        const fileTypes = Array.from(files).map(f => {
          const ext = f.name.split('.').pop()?.toLowerCase() || 'unknown'
          return ext
        }).join(',')

        vm.$usageTracker?.trackEvent('file-drop', 'drop', 'user-file-drop', {
          fileCount: files.length,
          fileTypes: fileTypes,
          totalSize: Array.from(files).reduce((sum, f) => sum + f.size, 0)
        })

        // ★ 新しい統合ダイアログを開く
        vm.$store.commit('openFileDropFlow', { files: Array.from(files), mode: 'user-drop' });
      });
    }

    // -----------------------------------------------------------------------------------------------------------
    map.addSource('zones-source', {
      type: 'vector',
      url: 'pmtiles://https://kenzkenz.net/pmtiles/zahyokei/zahyokei.pmtiles',
    });

    map.addLayer({
      id: 'zones-layer',
      type: 'fill',
      source: 'zones-source',
      'source-layer': 'polygon',
      paint: {
        'fill-color': '#0080',
        'fill-opacity': 0
      }
    });

    setTimeout(() => {
      const center = map.getCenter();
      const centerPoint = map.project([center.lng, center.lat]);
      // 画面中心地点でのフィーチャークエリ
      const features = map.queryRenderedFeatures(centerPoint, {
        layers: ['zones-layer']
      });
      if (features.length > 0) {
        const zoneFeature = features[0];
        const zone = zoneFeature.properties.zone;
        vm.$store.state.zahyokei = '公共座標' + zone + '系';
      }
    },1000)
    map.on('moveend', () => {
      const center = map.getCenter();
      const centerPoint = map.project([center.lng, center.lat]);
      // 画面中心地点でのフィーチャークエリ
      const features = map.queryRenderedFeatures(centerPoint, {
        layers: ['zones-layer']
      });
      if (features.length > 0) {
        const zoneFeature = features[0];
        const zone = zoneFeature.properties.zone;
        vm.$store.state.zahyokei = '公共座標' + zone + '系';
      } else {
        vm.$store.state.zahyokei = '';
      }
      vm.zoom = map.getZoom()
    });

    // -----------------------------------------------------------------------------------------------------------
    // 市区町村別人口ピラミッド - クリックでFWを開く
    map.on('click', 'oh-population-pyramid-polygon', (e) => {
      // ドローモード・断面図モード時は開かない
      if (vm.s_isDraw || vm.$store.state.elevationDrawMode) return
      if (e.features && e.features.length > 0) {
        const props = e.features[0].properties
        const municipalityCode = props.CODE5
        const municipalityName = props.NAME
        const prefName = props.N03_001 || ''
        if (municipalityCode) {
          // ハイライトレイヤーのフィルターを更新
          if (map.getLayer('oh-population-pyramid-highlight')) {
            map.setFilter('oh-population-pyramid-highlight', ['==', ['get', 'CODE5'], municipalityCode])
          }
          vm.$store.commit('addFWInstance', {
            type: 'municipality-pyramid',
            data: { municipalityCode, municipalityName, prefName }
          })
        }
      }
    })
    // ホバー時カーソル変更
    map.on('mouseenter', 'oh-population-pyramid-polygon', () => {
      map.getCanvas().style.cursor = 'pointer'
    })
    map.on('mouseleave', 'oh-population-pyramid-polygon', () => {
      map.getCanvas().style.cursor = ''
    })
    // 空白クリックでハイライト解除
    map.on('click', (e) => {
      if (vm.s_isDraw || vm.$store.state.elevationDrawMode) return
      // 人口ピラミッドレイヤーが表示されていない場合はスキップ
      if (!map.getLayer('oh-population-pyramid-polygon')) return
      // クリック位置に市区町村フィーチャーがあるか確認
      const features = map.queryRenderedFeatures(e.point, { layers: ['oh-population-pyramid-polygon'] })
      if (features.length === 0) {
        // 空白クリック - ハイライト解除（存在しないコードでマッチさせない）
        if (map.getLayer('oh-population-pyramid-highlight')) {
          map.setFilter('oh-population-pyramid-highlight', ['==', ['get', 'CODE5'], '__NONE__'])
        }
      }
    })

    // -----------------------------------------------------------------------------------------------------------
    // マップ上でポリゴンをクリックしたときのイベントリスナー（新パフォーマンス改善版）
    let highlightCounter = 0;
    map.on('click', 'oh-homusyo-2025-polygon', (e) => {
      if (vm.s_isDraw || vm.$store.state.elevationDrawMode) return
      if (vm.$store.state.isDrawPoint) return;
      if (!vm.$store.state.isRenzoku) return;
      if (map.getLayer('oh-point-layer')) return;
      // 全国地番図公開マップ（都市地番図）が重なっている場合は2025のハイライトをスキップ
      // クリック地点の全フィーチャーを取得して、都市地番図レイヤーがあるかチェック
      const allFeaturesAtPoint = map.queryRenderedFeatures(e.point);
      const hasChibanzuFeature = allFeaturesAtPoint.some(f => {
        const id = f.layer?.id;
        return id === 'oh-city-geojson-poligon-layer' ||
          id === 'oh-city-pmtiles-poligon-layer' ||
          id?.startsWith('oh-chibanzu-') ||
          (id?.startsWith('oh-chiban-') && id?.endsWith('-layer')) || // oh-chiban-1411-福岡県-大牟田市-layer形式
          (id?.startsWith('oh-city-') && !id?.includes('label') && !id?.includes('line'));
      });
      if (hasChibanzuFeature) {
        return; // 全国地番図公開マップを優先、2025ハイライトをスキップ
      }
      if (e.features && e.features.length > 0) {
        // クリック座標からGeoJSONポイントを作る
        const point = turf.point([e.lngLat.lng, e.lngLat.lat]);
        // クリック点を実際に内包するポリゴンだけ抽出
        const insidePolygons = e.features.filter(f => turf.booleanPointInPolygon(point, f));

        // 最小面積のポリゴンを選択（優先はinsidePolygons→なければe.features全体から）
        let targetFeature = null;
        if (insidePolygons.length > 0) {
          targetFeature = insidePolygons.reduce((a, b) => turf.area(a) < turf.area(b) ? a : b);
        } else {
          targetFeature = e.features.reduce((a, b) => turf.area(a) < turf.area(b) ? a : b);
        }

        const targetId = `${targetFeature.properties['筆ID']}_${targetFeature.properties['地図名']}`;

        pushSelectionUndo(store)
        if (vm.$store.state.highlightedChibans.has(targetId)) {
          // すでに選択されている場合は解除
          vm.$store.state.highlightedChibans.delete(targetId);
          vm.$store.state.highlightedFeatures.delete(targetId);
          vm.$store.state.lastHighlightedChiban = null
        } else {
          // 新しいIDを追加
          vm.$store.state.highlightedChibans.add(targetId);
          // GeoJSONフィーチャーとして保存（geometry含む）
          vm.$store.state.highlightedFeatures.set(targetId, {
            type: 'Feature',
            geometry: targetFeature.geometry,
            properties: { ...targetFeature.properties, _targetId: targetId }
          });
          vm.$store.state.lastHighlightedChiban = targetId;
        }
        // 旧ハイライトシステム（setPaintProperty方式）で全タイルのハイライトを更新
        highlightSpecificFeatures2025(map, 'oh-homusyo-2025-polygon');
      }
    });

    map.on('click', 'oh-amx-a-fude', (e) => {
      if (!vm.$store.state.isRenzoku) return
      if (map.getLayer('oh-point-layer')) return
      if (e.features && e.features.length > 0) {
        // const targetId = `${e.features[0].properties['丁目コード']}_${e.features[0].properties['小字コード']}_${e.features[0].properties['地番']}`;
        const targetId = `${e.features[0].properties['地番区域']}_${e.features[0].properties['地番']}`;
        pushSelectionUndo(store)
        if (vm.$store.state.highlightedChibans.has(targetId)) {
          // すでに選択されている場合は解除
          vm.$store.state.highlightedChibans.delete(targetId);
        } else {
          // 新しいIDを追加
          vm.$store.state.highlightedChibans.add(targetId);
        }
        highlightSpecificFeatures(map,'oh-amx-a-fude');
      }
    });
    map.on('click', 'oh-chibanzu2024', (e) => {
      if (e.features && e.features.length > 0) {
        const targetId = `${e.features[0].properties['id']}`;
        pushSelectionUndo(store)
        if (vm.$store.state.highlightedChibans.has(targetId)) {
          // すでに選択されている場合は解除
          vm.$store.state.highlightedChibans.delete(targetId);
        } else {
          // 新しいIDを追加
          vm.$store.state.highlightedChibans.add(targetId);
        }
        highlightSpecificFeaturesCity(map,'oh-chibanzu2024');
      }
    });
    map.on('click', 'oh-fukushimachiban', (e) => {
      if (e.features && e.features.length > 0) {
        const targetId = `${e.features[0].properties['X']}_${e.features[0].properties['Y']}`;
        pushSelectionUndo(store)
        if (vm.$store.state.highlightedChibans.has(targetId)) {
          // すでに選択されている場合は解除
          vm.$store.state.highlightedChibans.delete(targetId);
        } else {
          // 新しいIDを追加
          vm.$store.state.highlightedChibans.add(targetId);
        }
        highlightSpecificFeaturesCity(map,'oh-fukushimachiban');
      }
    });
    map.on('click', 'oh-iwatapolygon', (e) => {
      if (e.features && e.features.length > 0) {
        const targetId = `${e.features[0].properties['SKSCD']}_${e.features[0].properties['AZACD']}_${e.features[0].properties['TXTCD']}`;
        pushSelectionUndo(store)
        if (vm.$store.state.highlightedChibans.has(targetId)) {
          // すでに選択されている場合は解除
          vm.$store.state.highlightedChibans.delete(targetId);
        } else {
          // 新しいIDを追加
          vm.$store.state.highlightedChibans.add(targetId);
        }
        highlightSpecificFeaturesCity(map,'oh-iwatapolygon');
      }
    });
    map.on('click', 'oh-narashichiban', (e) => {
      if (e.features && e.features.length > 0) {
        const targetId = `${e.features[0].properties['土地key']}_${e.features[0].properties['大字cd']}`;
        pushSelectionUndo(store)
        if (vm.$store.state.highlightedChibans.has(targetId)) {
          // すでに選択されている場合は解除
          vm.$store.state.highlightedChibans.delete(targetId);
        } else {
          // 新しいIDを追加
          vm.$store.state.highlightedChibans.add(targetId);
        }
        highlightSpecificFeaturesCity(map,'oh-narashichiban');
      }
    });
    map.on('click', 'oh-kitahiroshimachiban', (e) => {
      if (e.features && e.features.length > 0) {
        const targetId = `${e.features[0].properties['Aza']}_${e.features[0].properties['Chiban']}_${e.features[0].properties['Edaban']}`;
        pushSelectionUndo(store)
        if (vm.$store.state.highlightedChibans.has(targetId)) {
          // すでに選択されている場合は解除
          vm.$store.state.highlightedChibans.delete(targetId);
        } else {
          // 新しいIDを追加
          vm.$store.state.highlightedChibans.add(targetId);
        }
        highlightSpecificFeaturesCity(map,'oh-kitahiroshimachiban');
      }
    });
    map.on('click', 'oh-kunitachishi', (e) => {
      if (e.features && e.features.length > 0) {
        const targetId = `${e.features[0].properties['id']}`;
        pushSelectionUndo(store)
        if (vm.$store.state.highlightedChibans.has(targetId)) {
          // すでに選択されている場合は解除
          vm.$store.state.highlightedChibans.delete(targetId);
        } else {
          // 新しいIDを追加
          vm.$store.state.highlightedChibans.add(targetId);
        }
        highlightSpecificFeaturesCity(map,'oh-kunitachishi');
      }
    });
    map.on('click', 'oh-fukuokashichiban', (e) => {
      if (e.features && e.features.length > 0) {
        const targetId = `${e.features[0].properties['id']}`;
        pushSelectionUndo(store)
        if (vm.$store.state.highlightedChibans.has(targetId)) {
          // すでに選択されている場合は解除
          vm.$store.state.highlightedChibans.delete(targetId);
        } else {
          // 新しいIDを追加
          vm.$store.state.highlightedChibans.add(targetId);
        }
        highlightSpecificFeaturesCity(map,'oh-fukuokashichiban');
      }
    });



    if (vm.$store.state.uploadedImage) {


    }
  })
  // on loadここまで-------------------------------------------------------------
  const layers = ['oh-chibanzu-室蘭市', 'oh-chibanzu-ニセコ町', 'oh-chibanzu-音更町',
    'oh-chibanzu-鹿角市', 'oh-chibanzu-舟形町', 'oh-chibanzu-利根町','oh-chibanzu-小平市',
    'oh-chibanzu-町田市','oh-chibanzu-静岡市','oh-chibanzu-磐田市','oh-chibanzu-半田市',
    'oh-chibanzu-京都市','oh-chibanzu-長岡京市','oh-chibanzu-岸和田市','oh-chibanzu-泉南市',
    'oh-chibanzu-西宮市','oh-chibanzu-加古川市','oh-chibanzu-佐用町','oh-chibanzu-奈良市',
    'oh-chibanzu-坂出市','oh-chibanzu-善通寺市','oh-chibanzu-長与町','oh-chibanzu-福島市',
    'oh-chibanzu-北広島市','oh-chibanzu-国立市','oh-chibanzu-福岡市','oh-chibanzu-越谷市',
    'oh-chibanzu-福山市','oh-chibanzu-深谷市','oh-chibanzu-伊丹市','oh-chibanzu-豊中市',
    'oh-chibanzu-姫路市','oh-chibanzu-仙台市','oh-chibanzu-高崎市','oh-chibanzu-旭川市',
    'oh-chibanzu-東村山市','oh-chibanzu-城陽市','oh-chibanzu-大山崎町','oh-chibanzu-川西市',
    'oh-chibanzu-香芝市','oh-chibanzu-直方市','oh-chibanzu-西粟倉村','oh-chibanzu-潮来市',
    'oh-chibanzu-甲府市','oh-chibanzu-名古屋市','oh-chibanzu-唐津市']
  layers.forEach(layer => {
    map.on('click', layer, (e) => {
      if (vm.s_isDraw || vm.$store.state.elevationDrawMode) return
      if (!vm.$store.state.isRenzoku) return
      if (map.getLayer('oh-point-layer')) return
      if (e.features && e.features.length > 0) {
        // 最小面積のポリゴンを選ぶ
        let smallestFeature = e.features[0];
        let minArea = turf.area(e.features[0]);
        for (let feature of e.features) {
          const area = turf.area(feature);
          if (area < minArea) {
            smallestFeature = feature;
            minArea = area;
          }
        }
        let targetId
        // Shozai + Chiban がある場合はそれを優先（浜松市FGB対応）
        if (smallestFeature.properties['Shozai'] !== undefined && smallestFeature.properties['Chiban'] !== undefined) {
          targetId = `${smallestFeature.properties['Shozai']}_${smallestFeature.properties['Chiban']}`;
        } else if (smallestFeature.properties['id'] !== undefined) {
          targetId = `${smallestFeature.properties['id']}`;
        } else {
          targetId = `${smallestFeature.properties['oh3id']}`;
        }
        pushSelectionUndo(store)
        if (vm.$store.state.highlightedChibans.has(targetId)) {
          // すでに選択されている場合は解除
          vm.$store.state.highlightedChibans.delete(targetId);
          store.commit('setRightDrawer', false)
        } else {
          // 新しいIDを追加
          vm.$store.state.highlightedChibans.add(targetId);
          const lngLat = e.lngLat
          // smallestFeatureを使う（最小面積のポリゴン）
          // geometryを含めたディープコピーを保存（MapLibre featureはプロキシオブジェクトなのでそのまま保存するとgeometryが失われる）
          store.state.popupFeature = {
            type: smallestFeature.type,
            geometry: smallestFeature.geometry ? JSON.parse(JSON.stringify(smallestFeature.geometry)) : null,
            properties: smallestFeature.properties ? JSON.parse(JSON.stringify(smallestFeature.properties)) : {}
          }
          store.state.popupFeatureProperties = smallestFeature.properties
          store.state.popupFeatureCoordinates = [lngLat.lng, lngLat.lat]
          async function address() {
            const address = await LngLatToAddress(lngLat.lng, lngLat.lat);
            store.state.rightDrawerTitle = address
          }
          address()
          store.commit('setChibanzuDrawer', false)
          store.commit('setRightDrawer', true)
        }
        highlightSpecificFeaturesCity(map,layer);
      }
    });
  });

  vm.compass = new CompassControl({
    visible: false // ボタンを非表示にする
  })
  map.addControl(vm.compass)

  // ストリートビューカバレッジタイル（青い線）
  function addStreetViewCoverage() {
    if (map.getSource('street-view-coverage')) return
    const svVisible = store.state.streetViewFlg
    map.addSource('street-view-coverage', {
      type: 'raster',
      tiles: ['https://mts1.googleapis.com/vt?hl=ja&lyrs=svv|cb_client:apiv3&style=40,18&x={x}&y={y}&z={z}'],
      tileSize: 256,
      maxzoom: 21,
    })
    map.addLayer({
      id: 'street-view-coverage-layer',
      type: 'raster',
      source: 'street-view-coverage',
      layout: { visibility: svVisible ? 'visible' : 'none' },
      paint: { 'raster-opacity': 0.7 },
    })
    if (svVisible) map.moveLayer('street-view-coverage-layer')
  }
  if (map.isStyleLoaded()) {
    addStreetViewCoverage()
  } else {
    map.once('idle', addStreetViewCoverage)
  }

  map.on('moveend', () => {
    zenkokuChibanzuAddLayer(map,map.getZoom())
    // ストリートビューカバレッジを常に最上位に維持
    if (store.state.streetViewFlg && map.getLayer('street-view-coverage-layer')) {
      map.moveLayer('street-view-coverage-layer')
    }
  })
  // zenkokuChibanzuAddLayer(map,map.getZoom()) // dialog.layer.vueの552行くらいに移動
  //on load終了----------------------------------------------------------------------------------------------------
})

}
